/**
 * Project Detail — STAFF + Admin track (Phase 1 · Step 6).
 *
 * The staff work surface for one project. Resolves stages + workflow tabs
 * + Devta/Marma surface gating + follow-up + money gates entirely from the
 * project's LOCKED engagementTemplate (no forked tier logic — reuses
 * stageRailFor / tabsFor / isSurfaceUnlocked / canEnterStage). Tabs that a
 * tier doesn't have are correctly absent; later-sprint surfaces render
 * dimmed (the Sprint-2 "don't read as broken" discipline). The work/AUO
 * canvas + Composer launch + Day-44 are framed here and link into the
 * already-built Conversation surface for the live demo project.
 *
 * Client never reaches this route (RequireAuth roles={['admin','staff']}).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import ProjectThreadPanel from '../components/ProjectThreadPanel';
import ProjectCommsSheet from '../components/ProjectCommsSheet';
import IntakeLinkSendBar from '../components/IntakeLinkSendBar';
import { canCommitToProject, type Role } from '../lib/roles';
import { usePageChrome } from '../lib/pageChromeContext';
import { Box, Btn3, Card, EmptyNote, StageRail, StageTabBar, Tag } from '../components/ui';
import {
  L1A_TAB_ITEMS,
  defaultL1ATab,
  resolveL1ATabKey,
  type L1ATabKey,
  type StageStatus,
} from '../lib/l1aTabs';
import { deriveL1AStageStates, toBarStatus, type L1ACandidate } from '../lib/l1aStages';
import StageScreen from '../components/l1a/StageScreen';
import OverviewScreen from '../components/l1a/OverviewScreen';
import OwnerReviewScreen from '../components/l1a/OwnerReviewScreen';
import AstroVastuScreen from '../components/l1a/AstroVastuScreen';
import {
  watchAstroVastuConfirmation,
  hasAstroSubject,
  type AstroVastuConfirmation,
} from '../lib/astroVastuConfirm';
import { GroupPicker, groupToOption, AstroLinkBadge, type GroupOption } from '../components/GroupPicker';
import { ClientPicker } from '../components/ClientPicker';
import { listenFamilies, type FamilyGroup } from '../lib/familyStore';
import DeliveredScreen from '../components/l1a/DeliveredScreen';
import ClosingCallScreen from '../components/l1a/ClosingCallScreen';
import {
  seedProjects,
  type DemoProject,
  moneyViewOf,
  railOf,
  templateOf,
} from '../lib/trackDemo';
import { money } from '../lib/moneyModel';
import { useToast } from '../components/Toast';
import { canonicalServiceNameFor, canonicalTierOptions } from '../lib/leadQuote';
import type { Tier } from '../lib/engagementTemplate';
import {
  addParticipant,
  listenClients,
  listenProjects,
  removeParticipant,
  seedParticipantsFromLegacy,
  updateProject,
  relabelProjectVp,
  validateVpEdit,
  archiveProject,
  setProjectLinkedGroup,
  setProjectLinkedContacts,
  projectLabel,
  effectiveClearedGateIds,
  unlinkPropertyFromProject,
  type ClientRow,
  type LiveClient,
  type LiveProject,
  type PropertyRef,
} from '../lib/projectStore';
import { participantLabel, isParticipantRole, type ParticipantRole } from '../lib/contactModel';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useClientNames } from '../lib/useClientNames';
import {
  listenPaymentSettings,
  setProjectPaymentMode,
  resolvePaymentMode,
  paymentModeLabel,
  PAYMENT_MODE_OPTIONS,
  type PaymentMode,
} from '../lib/paymentMode';
import {
  uploadProjectFile,
  listenProjectFiles,
  archiveProjectFile,
  humanSize,
  type ProjectFile,
} from '../lib/projectFiles';
import { imageFloorPlanSources, type FloorPlanSource } from '../lib/floorPlan';
import StaffTechnicalPanel from '../components/StaffTechnicalPanel';
import ProjectErrorBoundary from '../components/ProjectErrorBoundary';
import ReportComposerShell from '../components/ReportComposerShell';
import EvaluationWorkspace from '../components/EvaluationWorkspace';
import {
  listenAllProperties,
  createProperty,
} from '../lib/propertyStore';
import { type LiveProperty } from '../lib/propertyModel';
import { loadIntake, savePropertyLink } from '../lib/intake';
import {
  APPROVED_DEFAULTS,
  PORTAL_ROLES,
  PORTAL_SECTIONS,
  canSee,
  type PortalRole,
  type PortalSection,
  type VisibilityMatrix,
  type ProjectVisibilityOverrides,
} from '../lib/visibilityModel';
import { getVisibilityMatrix } from '../lib/visibilityStore';
import {
  setProjectVisibilityOverride,
  clearProjectVisibilityOverride,
  clearAllProjectOverrides,
} from '../lib/projectStore';

/** Live converted project → DemoProject shape (track screens need these). */
function liveToRow(p: LiveProject): DemoProject {
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tier: p.tierCode,
    activeStageKey: p.activeStageKey,
    assigneeUid: p.assigneeUid,
    assigneeName: p.assigneeName,
    valueINR: p.valueINR,
    // BUG A FIX (2026-06-16) — cleared from the canonical source (moneyPaid ∪
    // legacy clearedGateIds), so the project-page gates + money picture agree
    // with the Record-payment modal after a payment is recorded.
    clearedGateIds: effectiveClearedGateIds(p),
    openProposals: 0,
    // AGREED-PRICE FIX — carry currency + the agreed per-gate amounts so the
    // money picture renders the right symbol + the agreed figure (not the
    // ₹11,000 template default wearing a "$").
    currency: p.currency,
    gateAmounts: p.gateAmounts,
  };
}
import {
  canEnterStage,
  isSurfaceUnlocked,
  tabsFor,
  type GateState,
} from '../lib/engagementTemplate';
import {
  canOverrideGate,
  recordGateOverride,
  type GateKind,
} from '../lib/gateOverride';
import {
  composeReport,
  approveReport,
  deliverReport,
  canGenerateReport,
  toDeliverableTier,
  reportStatusOf,
  listenProjectReports,
  latestReportForTier,
  getDraftArtefactUrl,
  type ReportDoc,
  type ReportStatus,
} from '../lib/reportDelivery';
import {
  loadPropertiesIntake,
  saveProperties,
  clearCandidatePropertyStamp,
  type PropertyInput,
} from '../lib/staffIntake';
import {
  isCandidateLinkVisible,
  joinCandidatesToLinkedProperties,
  stampCandidateProperty,
} from '../lib/candidatePropertyJoin';
import { linkPropertyAsCandidate } from '../lib/linkPropertyAsCandidate';
import { gridSummary, type GridSummary } from '../lib/candidateGrid';
import { hydrateCandidatesWithPropertyGrids } from '../lib/hydrateCandidateGrids';
import { VerdictField, ReportContextRest } from '../components/l1a/ReportContextCard';
import ComparativePanel from '../components/l1a/ComparativePanel';

export default function ProjectDetail() {
  const { pid } = useParams();
  const { role, user, clientId: sessionClientId } = useAuth();
  // Client portal session (visibility matrix v1, AD-010) — clientId is
  // non-null only for client custom-token sessions. Drives the money-section
  // summary view + (inside ProjectThreadPanel) the clientVisible message filter.
  const isClientSession = Boolean(sessionClientId);
  const { toast } = useToast();
  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'You';
  const [showEdit, setShowEdit] = useState(false);
  const [showSendLink, setShowSendLink] = useState(false);
  // L1A-only deep-link routing (`?tab=`). Additive + L1A-scoped — the non-L1A
  // `tab` useState (below, ~line 290) is untouched. Hoisted here so the hook
  // order is stable (it must run before the not-found early-return).
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Page identity → top chrome bar (owner ask 2026-06-28) ───────────────
  // The identity (Client · VP · Tier + Fee/Paid/Due + assignee) is no longer a
  // separate sticky strip in the working area. It is PUBLISHED up into the
  // global `.phead` via usePageChrome() below, where it fills the previously
  // vacant centre. On /project/ routes AppShell suppresses its generic <h1>
  // (the published title takes its place) and the working area starts straight
  // at the action buttons. <DetailIdentityHeader> is kept for other pages but
  // is no longer mounted here.

  // Resolve from LIVE projects first (real converted + imported-master
  // projects), then the demo scaffold ONLY if the demo seed is enabled
  // (#992 — default off). When nothing matches we render a neutral
  // "not found" state instead of falling back to a fake demo project.
  const [live, setLive] = useState<LiveProject[]>([]);
  // True once the projects stream has returned at least once. Lets a child
  // (GridStatusRollup) tell "still loading" apart from "loaded, no client" so it
  // never spins forever on a null clientId (clean-flow spec §4).
  const [, setProjectsLoaded] = useState(false);
  useEffect(() => {
    const unsub = listenProjects(setLive, {
      onReady: () => setProjectsLoaded(true),
      onErr: () => setProjectsLoaded(true), // a read failure must not wedge the UI
    });
    return unsub;
  }, []);
  // Project files (lifted from FilesPanel so the floor tracer can ALSO offer
  // already-uploaded images as floor plans — one listener feeds both). null =
  // still loading.
  const [files, setFiles] = useState<ProjectFile[] | null>(null);
  useEffect(() => {
    if (!pid) return;
    const unsub = listenProjectFiles(pid, setFiles, () => {});
    return unsub;
  }, [pid]);
  // Image files that are NOT archived → the "Choose from project files"
  // picker sources (PDFs/docs filtered out — a raster is needed to trace).
  const projectImageFiles: FloorPlanSource[] = imageFloorPlanSources(
    (files ?? [])
      .filter((f) => !f.archived)
      .map((f) => ({
        fileId: f.fileId,
        fileName: f.fileName,
        downloadURL: f.downloadURL,
        storagePath: f.storagePath,
        contentType: f.contentType,
      })),
  );
  // Properties — for the Property linking panel (Module 4, AD-013).
  const [allProperties, setAllProperties] = useState<LiveProperty[]>([]);
  useEffect(() => {
    const unsub = listenAllProperties(setAllProperties);
    return unsub;
  }, []);

  // Org-default payment mode (owner directive 2026-05-19). The effective
  // mode = project override ?? org default ?? 'payment_before'.
  const [globalMode, setGlobalMode] = useState<PaymentMode>(
    'payment_before',
  );
  useEffect(() => {
    const unsub = listenPaymentSettings(setGlobalMode, () => {});
    return unsub;
  }, []);
  const liveMatch = live.find((x) => x.pid === pid);
  const effectiveMode: PaymentMode = resolvePaymentMode(
    liveMatch?.paymentMode ?? null,
    globalMode,
  );
  const isAdmin = role === 'admin';
  const resolved: DemoProject | null = liveMatch
    ? liveToRow(liveMatch)
    : (seedProjects().find((x) => x.pid === pid) ?? null);
  // Stable placeholder so the hooks below never see undefined (hook order
  // must not depend on data); the real early-return is after all hooks.
  const p: DemoProject = resolved ?? {
    pid: pid ?? '—',
    vp: pid ?? '—',
    clientName: '—',
    clientNo: '—',
    tier: 'L1B',
    activeStageKey: '',
    assigneeUid: 'unassigned',
    assigneeName: 'Unassigned',
    valueINR: 0,
    clearedGateIds: [],
    openProposals: 0,
  };
  const tpl = templateOf(p);
  const m = moneyViewOf(p);
  // Canonical client name — the project's `clientName` is a display cache
  // that can drift; the clients/{id} master is the single source of truth.
  const { resolveName, masters } = useClientNames();
  const clientName = resolveName(
    liveMatch?.clientId || p.clientNo,
    p.clientName,
  );
  // clean-flow spec §4.1 — clientNo↔clientId fallback. Some projects carry only
  // a `clientNo` (C##### series) and a null `clientId`; resolve it through the
  // clients master before any child declares "no client". Falls back to clientNo
  // verbatim (the convert path defaults clientId := clientNo) so a legacy doc with
  // no master still resolves.
  const resolvedClientId: string | null =
    liveMatch?.clientId ||
    (liveMatch?.clientNo
      ? masters.find((mm) => mm.clientNo === liveMatch.clientNo)?.cid ??
        liveMatch.clientNo
      : null) ||
    null;
  const [tab, setTab] = useState<string>('messages');
  // Mobile-only Conversation sheet (≤860px). On phones the persistent
  // `.project-comms-col` is display:none, so the thread is reached through a
  // green chat FAB that opens a full-height slide-up sheet mounting the REAL
  // ProjectThreadPanel (see the mobile block in components.css + <ProjectCommsSheet>).
  const [commsSheetOpen, setCommsSheetOpen] = useState(false);

  // Commit rights (owner-locked: assigned staff OR admin, no tier-fallback).
  // Demo: the signed-in staff is treated as the project's assignee uid via
  // the demo seam; admin always holds rights.
  const signedUid = role === 'staff' ? p.assigneeUid : user?.uid ?? null;
  const mayCommit = canCommitToProject(role, signedUid, p.assigneeUid);

  // Real gate verdict from the locked template (client-side UX — the
  // authoritative recheck is server-side, see recheckGate CF).
  const gateState: GateState = {
    moneyPaid: Object.fromEntries(p.clearedGateIds.map((id) => [id, true])),
    curationCleared: false,
    consultationDone: {},
  };

  const tabs = tabsFor(tpl);

  // L1A is the only tier that surfaces the Fee/Paid/Due money chip in the
  // identity (computed here so it's available to the chrome publish below, which
  // runs before the not-found early-return). Reused later for the flow gating.
  const isL1ATier = tpl.tier === 'L1A';

  // L1A `?tab=` navigation (L1A-scoped; replace, no history spam). Merges into
  // any existing query params and scrolls the screen to top on select (mockup
  // go()). Real status-driven default lands in pass 4; pass 1 uses the stub.
  const goL1ATab = (key: L1ATabKey) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', key);
    setSearchParams(next, { replace: true });
    // The vertical scroller is `.work` (height:100vh; overflow-y:auto) — NOT
    // window — so reset THAT on a tab switch. The pinned stage-bar stays put.
    document.querySelector('.work')?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // "✦ Ask" entry — no project-scoped assistant is wired yet, so surface the
  // Conversation as the stub target. On desktop the persistent right column is
  // present → scroll it into view. On mobile (≤860px, matching the AppShell +
  // .project-comms-col breakpoint) that column is display:none, so open the
  // full-height Conversation sheet instead.
  const openAsk = () => {
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 860px)').matches;
    if (isMobile) {
      setCommsSheetOpen(true);
      return;
    }
    document
      .querySelector('.project-comms-col')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Pin the L1A stage-bar directly BELOW the sticky page header (.phead). Its
  // height is dynamic (the published identity adds a line; mobile drops chips),
  // so measure it and expose it as `--l1a-bar-top` for the `.stagebar` sticky
  // offset. ResizeObserver keeps it correct as the header reflows.
  useEffect(() => {
    if (!isL1ATier) return;
    const phead = document.querySelector('.phead') as HTMLElement | null;
    if (!phead) return;
    const apply = () => {
      document.documentElement.style.setProperty(
        '--l1a-bar-top',
        `${phead.offsetHeight}px`,
      );
    };
    apply();
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(phead);
    window.addEventListener('resize', apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', apply);
      document.documentElement.style.removeProperty('--l1a-bar-top');
    };
  }, [isL1ATier]);

  // Publish the breadcrumb + identity UP into the global AppShell `.phead`
  // (owner ask 2026-06-28). Breadcrumb → top-left `.crumb` (Work › Projects ›
  // {project name}, "Projects" linking to the all-projects list, the project
  // name the current non-link crumb). Identity → the vacant `.phead` centre:
  //   title = client name; chips = VP · tier pill; meta = money chip + assignee.
  // This replaces the working-area sticky <DetailIdentityHeader>: the identity
  // now lives in the top chrome bar and the work surface starts at the action
  // buttons. Published null on the not-found path so the hook stays
  // unconditional; cleared automatically on unmount.
  usePageChrome(
    resolved
      ? {
          breadcrumb: [
            { label: 'Work' },
            { label: 'Projects', href: '/all-projects' },
            { label: projectLabel(p.projectName, p.vp, tpl.tier) },
          ],
          title: clientName,
          chips: (
            <>
              <span aria-hidden style={{ color: 'var(--ink-mute)' }}>·</span>
              <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{p.vp}</span>
              <span className="ptag">{tpl.tier}</span>
            </>
          ),
          // L1A: the Fee/Paid/Due money chip LEAVES the header (it relocates to
          // the Overview screen — see OverviewScreen, where data-testid=
          // "l1a-money-chip" is preserved). The compact right cluster becomes
          // assignee chip + Edit project (→ Settings tab) + ✦ Ask. Every other
          // tier keeps the byte-identical assignee span (the money chip was
          // already isL1ATier-gated, so the non-L1A output is unchanged).
          meta: isL1ATier ? (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span className="assignee-chip">
                Assignee{' '}
                <span className="avatar">
                  {(p.assigneeName || '?').trim().charAt(0).toUpperCase()}
                </span>
              </span>
              <Btn3 intent="ghost" size="sm" onClick={() => goL1ATab('settings')}>
                Edit project
              </Btn3>
              <Btn3 intent="ghost" size="sm" className="ask" onClick={openAsk}>
                ✦ Ask about this project
              </Btn3>
            </div>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>
              Assignee <b style={{ color: 'var(--ink)' }}>{p.assigneeName}</b>
            </span>
          ),
        }
      : null,
  );

  // Derive the intake doc ID from the project's tier prefix (L1A → L1A_R1, L2B → L2B_R1).
  // toDeliverableTier strips the long display suffix ("L1A · Selection Path…") to 'L1A'.
  // The RAW bare tier code (e.g. "L1C", "L2C") — before the deliverable narrowing.
  const bareTierCode = (liveMatch?.tier || p.tier || '').split(/\s*[·•]/)[0].trim().toUpperCase();
  const bareTier = toDeliverableTier(bareTierCode);
  const intakeTier = bareTier ? `${bareTier}_R1` : undefined;

  // ── L1A status-derivation reads (PASS 4) — hoisted into ProjectDetail so the
  // stage-bar dots, every screen pill, and the Overview default all read ONE
  // derivation. Read-only (the same listeners L1AFlow used): the candidate
  // intake (loadPropertiesIntake + read-time grid hydration) + a live report
  // stream. Gated on isL1ATier inside the effects so other tiers do no extra
  // reads. Hooks stay unconditional (before the not-found early-return).
  const [l1aCands, setL1aCands] = useState<L1ACandidate[] | null>(null);
  const [l1aRecommendedId, setL1aRecommendedId] = useState('');

  // Shared LINK-CORRECTION invalidation key (owner ruling 2026-07-03: "a
  // correction must propagate"). Bumped by EVERY Intake-tab link write — group
  // link/unlink, contact link/unlink, property link/unlink/candidate-stamp —
  // so every link-derived read on this page re-resolves in-session, no reload.
  // Consumers: the candidate-intake read below (grid summary + stage
  // derivation) and the Astro-Vastu tab's resolve (refreshKey). Declared above
  // the early return to keep hook order stable. (Supersedes the group/contact-
  // only `astroRefreshNonce`.)
  const [linkRefreshNonce, setLinkRefreshNonce] = useState(0);

  // The raw ?tab= param — read up here (the tab bar re-reads it below) so the
  // candidate-intake effect ALSO re-fetches on every stage-tab activation:
  // tab switches are the natural correction rhythm (fix a link at Intake →
  // open Evaluate/Compare → the page re-reads). The stage screens themselves
  // swap-mount per activation (`l1a-stage-screens` renders ONE screen), so
  // screens that fetch their own data are already per-activation; this covers
  // the page-level candidate read.
  const rawTab = searchParams.get('tab');

  useEffect(() => {
    if (!isL1ATier || !resolvedClientId) {
      setL1aCands(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const r = await loadPropertiesIntake(
          { clientId: resolvedClientId, projectId: p.pid },
          intakeTier,
        );
        const hydrated = await hydrateCandidatesWithPropertyGrids(r.form.properties);
        if (!alive) return;
        setL1aCands(hydrated as L1ACandidate[]);
        setL1aRecommendedId(r.recommendedPropertyId || '');
      } catch {
        if (alive) setL1aCands([]);
      }
    })();
    return () => {
      alive = false;
    };
    // linkRefreshNonce = in-session link-correction invalidation; rawTab =
    // re-fetch on stage-tab activation. On a re-fetch the previous value stays
    // rendered until the fresh read lands (no null reset, no loading flash).
  }, [isL1ATier, resolvedClientId, p.pid, intakeTier, linkRefreshNonce, rawTab]);

  const l1aDeliverableTier = toDeliverableTier(p.tier);
  const [l1aReports, setL1aReports] = useState<ReportDoc[]>([]);
  useEffect(() => {
    if (!isL1ATier || !resolvedClientId || !l1aDeliverableTier) {
      setL1aReports([]);
      return;
    }
    const unsub = listenProjectReports(resolvedClientId, p.pid, setL1aReports, () => {});
    return unsub;
  }, [isL1ATier, resolvedClientId, p.pid, l1aDeliverableTier]);
  const l1aLatest = useMemo(
    () => (l1aDeliverableTier ? latestReportForTier(l1aReports, l1aDeliverableTier) : null),
    [l1aReports, l1aDeliverableTier],
  );
  const l1aReportStatus = l1aLatest ? reportStatusOf(l1aLatest) : null;

  // Astro-Vastu confirm tick (2026-07-03) — live `astro_vastu_confirmation`
  // off the project doc. ONE watched value feeds BOTH the stage-chip
  // derivation (astroConfirmed) and the tab's confirmed banner, so they can
  // never disagree. (The shared `live` project stream maps a fixed field set
  // in projectStore — a shared-tree file another lane is editing — so this
  // additive read is a self-contained doc watch instead.)
  const [astroConfirmation, setAstroConfirmation] =
    useState<AstroVastuConfirmation | null>(null);
  useEffect(() => {
    if (!pid) return;
    return watchAstroVastuConfirmation(pid, setAstroConfirmation);
  }, [pid]);
  // VOID-WHEN-NO-SUBJECT (owner bug #5, 2026-07-03 — VP678): a stored
  // confirmation only COUNTS while the project still carries an astro subject
  // (Group / Contacts / legacy astro links). Write-side now voids the field on
  // subject-removal (projectLinkedGroupBody/projectLinkedContactsBody); this
  // read-side gate covers dangling docs that predate that rule. ONE effective
  // value feeds BOTH consumers — the stage-chip derivation (astroConfirmed)
  // and the tab's confirmed banner — so they can never disagree. A REPLACED
  // subject (still non-empty) keeps the confirmation + its Re-confirm
  // affordance (536784c behaviour, unchanged).
  const effectiveAstroConfirmation = hasAstroSubject(liveMatch ?? null)
    ? astroConfirmation
    : null;

  // #992: no live match + no (gated) demo seed → neutral empty state,
  // never a fabricated demo project.
  if (!resolved) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Box title="Project not found">
          <EmptyNote title="Not in the live records">
            No project <b>{pid}</b> in the live records yet. Open it from
            the Clients &amp; Projects list once it has been created or
            imported.
          </EmptyNote>
        </Box>
      </div>
    );
  }

  // ── L1A clean-flow (Prompt 2): the 5 reused work panels, built ONCE as
  // elements (each in its own ProjectErrorBoundary) and placed either into the
  // L1AFlow accordion (tpl.tier === 'L1A') OR the legacy flat stack (every other
  // tier — byte-identical). Defining them once means a panel mounts in exactly
  // one branch, never both. (`isL1ATier` is computed above the early-return so
  // the chrome publish can use it.)

  const propertyLinkingEl = (
    <ProjectErrorBoundary surface="Candidate properties">
      <PropertyLinkingPanel
        projectId={p.pid}
        propertyRefs={liveMatch?.propertyRefs ?? []}
        allProperties={allProperties}
        onMsg={(msg) => toast(msg)}
        clientId={resolvedClientId}
        intakeTier={intakeTier}
        tierCode={p.tier}
        onLinkChange={() => setLinkRefreshNonce((n) => n + 1)}
      />
    </ProjectErrorBoundary>
  );

  // AVBP Group link (owner correction 2026-07-02) — stores the AVBP Group doc
  // id (`families/{id}`) on `project.linked_group_id`, NOT the astro store.
  // Bumped on every link/unlink so a currently-mounted Astro-Vastu tab
  // re-resolves against the newly-linked group (Forge resolves charts from each
  // member's birth details).
  const linkedFamilyEl = (
    <ProjectErrorBoundary surface="Linked group">
      <LinkedGroupPanel
        projectId={p.pid}
        groupId={liveMatch?.linked_group_id ?? null}
        onMsg={(msg) => toast(msg)}
        onLinkChange={() => setLinkRefreshNonce((n) => n + 1)}
      />
    </ProjectErrorBoundary>
  );

  // The EITHER/OR partner to the Linked-Group box: 1–2 individual AVBP Contacts
  // as the astro subject. Stores the AVBP Contact ids (`clients/{id}`) on
  // `project.linked_contact_ids`, NOT the astro store. Linking contacts clears
  // the group link (either/or, enforced in setProjectLinkedContacts) — the two
  // boxes reconcile through the live project stream. Shares linkRefreshNonce so
  // the Astro-Vastu tab re-resolves on any change here.
  const linkedContactsEl = (
    <ProjectErrorBoundary surface="Linked contacts">
      <LinkedContactsPanel
        projectId={p.pid}
        contactIds={liveMatch?.linked_contact_ids ?? null}
        groupLinked={!!liveMatch?.linked_group_id}
        onMsg={(msg) => toast(msg)}
        onLinkChange={() => setLinkRefreshNonce((n) => n + 1)}
      />
    </ProjectErrorBoundary>
  );

  const staffTechnicalEl = (
    <ProjectErrorBoundary surface="Technical intake">
      <StaffTechnicalPanel
        clientId={liveMatch?.clientId || null}
        projectId={p.pid}
        actor={{ uid: user?.uid ?? null, name: actorName }}
        isAdmin={isAdmin}
        onMsg={(msg) => toast(msg)}
        projectImageFiles={projectImageFiles}
        intakeTier={intakeTier}
        linkedPropertyIds={liveMatch?.propertyRefs?.map((r) => r.propertyId) ?? []}
        linkedPropertyRefs={liveMatch?.propertyRefs ?? []}
        hideGridTool
        hideReportContext={isL1ATier}
        evalChecklist={isL1ATier}
        // FIX D (owner bug 2026-07-03): the engagement-level "Site & orientation
        // (staff)" card is hidden on the L1A Evaluate mount — the L1A grid reads
        // the per-property north, and per-property editors already exist. Non-L1A
        // tiers keep the card.
        hideSiteOrientation={isL1ATier}
        // FIX A: a candidate REMOVE is a link-shape write — bump the shared
        // linkRefreshNonce so every stage tab re-resolves in-session.
        onLinkChange={() => setLinkRefreshNonce((n) => n + 1)}
      />
    </ProjectErrorBoundary>
  );

  const evaluationEl = (
    <ProjectErrorBoundary surface="Evaluation workspace">
      <EvaluationWorkspace
        clientId={liveMatch?.clientId || null}
        projectId={p.pid}
        tierCode={p.tier}
        role={role}
        actorUid={user?.uid ?? null}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  );

  // RETIRED 2026-07-01 — the folded findings-pool confirm (`evaluationFoldedEl`,
  // an <EvaluationWorkspace folded/> over findingsStore.confirmFinding) is
  // removed from the L1A Evaluate surface: it wrote a client-preview store the
  // deployed report never reads (a duplicate confirm). The authoritative confirm
  // is the per-AUO ✓ (fixtures[].status) inside the marking surface. The
  // EvaluationWorkspace component + findingsStore module are NOT deleted — non-
  // L1A tiers still mount `evaluationEl`.

  const reportComposerShellEl = (
    <ProjectErrorBoundary surface="Report composer">
      <ReportComposerShell
        clientId={liveMatch?.clientId || null}
        projectId={p.pid}
        tierCode={p.tier}
        role={role}
        actorUid={user?.uid ?? null}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  );

  const composerDeliveryEl = (
    <ProjectErrorBoundary surface="Composer & delivery">
      <ComposerDeliveryPanel
        pid={p.pid}
        clientId={liveMatch?.clientId || null}
        tierCode={p.tier}
        role={role}
        mayCommit={mayCommit}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  );


  // L1A Stage-1R Verdict body — the editable VERDICT field only (recommended pick
  // + statement + reasoning; disjoint saveVerdict → no clobber with the drawer's
  // report-context). The generate→approve→deliver panel is now the SEPARATE Report
  // step below. ReportComposerShell version history lives in the "more" drawer.
  // L1A Verdict — boxless flat variant (the ScreenHead carries the title, so the
  // inner element is a plain card with the recommended-property pick + statement,
  // matching the mockup #s-verdict). The non-L1A flat stack uses verdictReportEl
  // (Box-wrapped) — but that path doesn't mount the verdict on its own, so only
  // this flat variant is built. Disjoint saveVerdict (no report-context clobber).
  const verdictFieldFlatEl = (
    <ProjectErrorBoundary surface="Verdict">
      <Card>
        <VerdictField
          clientId={resolvedClientId}
          projectId={p.pid}
          intakeTier={intakeTier}
          actor={{ uid: user?.uid ?? null, via: 'staff' }}
          onMsg={(msg) => toast(msg)}
          onRecommendationChanged={setL1aRecommendedId}
        />
      </Card>
    </ProjectErrorBoundary>
  );

  // L1A "more" drawer — the rest of report-context (concerns · address · room
  // allocation); the verdict is in Step 5. Disjoint saveReportContextRest.
  const reportContextRestEl = (
    <ProjectErrorBoundary surface="Report context">
      <ReportContextRest
        clientId={resolvedClientId}
        projectId={p.pid}
        intakeTier={intakeTier}
        actor={{ uid: user?.uid ?? null, via: 'staff' }}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  );

  // L1A Step 5 — ONE report home (Pass-1 §1.4). Consolidates the three
  // previously-scattered report surfaces into the single Report step:
  //   1) the generate/approve/deliver/download panel (composerDeliveryEl);
  //   2) the version-history strip + phase mechanics, folded in from the
  //      drawer's ReportComposerShell (tier-gating stays intact, so L2/L3
  //      phases keep working — for L1A it's the single locked report line);
  //   3) the report-context (concerns · address · room allocation), folded in
  //      from the drawer's ReportContextRest behind a "Report details"
  //      disclosure (it's report-content input, not project settings).
  // Non-L1A is unaffected: the flat stack still mounts reportComposerShellEl
  // and composerDeliveryEl inline; only the L1A drawer duplicates are removed.
  const reportStepEl = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {composerDeliveryEl}
      {reportComposerShellEl}
      <details>
        <summary
          style={{
            cursor: 'pointer',
            padding: '12px 14px',
            fontSize: 13.5,
            fontWeight: 700,
            color: 'var(--ink)',
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-md, 13px)',
            background: 'var(--inset, #f6f7f9)',
            userSelect: 'none',
          }}
        >
          Report details — concerns · address · room allocation
        </summary>
        <div style={{ marginTop: 12 }}>{reportContextRestEl}</div>
      </details>
    </div>
  );

  // ── The non-flow panels (clean-flow §6). Built once; for L1A they relocate
  // into the "Project settings / more" drawer (RELOCATE, not delete); every
  // other tier renders them inline (byte-identical). The Payments block keeps
  // its client-session visibility branch verbatim (client sees the summary
  // only, never the gate-by-gate breakdown).
  const filesEl = (
    <ProjectErrorBoundary surface="Files">
      <FilesPanel
        pid={p.pid}
        files={files}
        actorUid={user?.uid ?? 'unknown'}
        actorName={actorName}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  );

  const participantsEl = liveMatch ? (
    <ProjectErrorBoundary surface="Participants">
      <ParticipantsPanel
        project={liveMatch}
        isAdmin={isAdmin}
        actorUid={user?.uid ?? null}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  ) : null;

  const accessEl = liveMatch ? (
    <ProjectErrorBoundary surface="Access / visibility">
      <AccessPanel
        project={liveMatch}
        isAdmin={isAdmin}
        actorUid={user?.uid ?? null}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>
  ) : null;

  const paymentsEl = (
    <ProjectErrorBoundary surface="Payments">
      {isClientSession ? (
        <Box title="Payment summary">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-soft)' }}>Agreed fee</span>
              <b>{money(m.valueINR, p.currency ?? 'INR')}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-soft)' }}>Paid</span>
              <b>{money(m.paidINR, p.currency ?? 'INR')}</b>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--ink-soft)' }}>Due</span>
              <b>{money(m.dueINR, p.currency ?? 'INR')}</b>
            </div>
            {(() => {
              const next = m.gates.find((g) => !g.cleared);
              return next ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    borderTop: '1px solid var(--rule)',
                    paddingTop: 6,
                    marginTop: 2,
                  }}
                >
                  <span style={{ color: 'var(--ink-soft)' }}>
                    Next milestone — {next.label}
                  </span>
                  <b>{money(next.amountINR, p.currency ?? 'INR')}</b>
                </div>
              ) : null;
            })()}
          </div>
        </Box>
      ) : (
        <Box
          title="Payment gates"
          note={
            effectiveMode === 'work_first'
              ? 'Work-first — payment tracked, NOT enforced (does not block delivery)'
              : 'Payment before delivery — money gate blocks stage entry'
          }
        >
          <PaymentModePanel
            pid={p.pid}
            projectOverride={liveMatch?.paymentMode ?? null}
            globalMode={globalMode}
            effectiveMode={effectiveMode}
            isAdmin={isAdmin}
            actorName={actorName}
            onMsg={(msg) => toast(msg)}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {m.gates.map((g) => {
              const verdict = canEnterStage(
                tpl,
                tpl.gates.find((x) => 'id' in x && x.id === g.id)?.blocksStageKey ?? '',
                gateState,
                effectiveMode,
              );
              return (
                <Card key={g.id} flat className="row-card">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      flexWrap: 'wrap',
                    }}
                  >
                    <b>{money(g.amountINR, p.currency ?? 'INR')}</b>
                    <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>
                      {g.label}
                    </span>
                    <span style={{ marginLeft: 'auto' }}>
                      {g.cleared ? (
                        <Tag>cleared</Tag>
                      ) : effectiveMode === 'work_first' ? (
                        <Tag kind="warn">due · tracked (work-first)</Tag>
                      ) : verdict.allowed ? (
                        <Tag kind="warn">due</Tag>
                      ) : (
                        <Tag kind="warn">blocks delivery</Tag>
                      )}
                    </span>
                  </div>
                  {!g.cleared && canOverrideGate(role) && (
                    <GateOverridePanel
                      projectId={p.pid}
                      gateKind="money"
                      gateId={g.id}
                      gateLabel={g.label}
                      valueINR={p.valueINR}
                      onMsg={(msg) => toast(msg)}
                    />
                  )}
                </Card>
              );
            })}
          </div>
        </Box>
      )}
    </ProjectErrorBoundary>
  );

  const followUpEl = (
    <Box title="Follow-up schedule" note="Resolved from engagementTemplate.followUp">
      {tpl.followUp.length === 0 ? (
        <EmptyNote title="No timed follow-up for this tier">
          {tpl.tier} defines no scheduled post-delivery touchpoint (per its
          FULLSCOPE doc).
        </EmptyNote>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tpl.followUp.map((f) => (
            <Card key={f.dayOffset} flat>
              <b>Day {f.dayOffset === 0 ? '0' : `+${f.dayOffset}`}</b> ·{' '}
              {f.label}
            </Card>
          ))}
        </div>
      )}
    </Box>
  );

  // L1A "Evaluate" — the MERGED stage (owner sign-off 2026-07-01): the old
  // separate `grids_charts` (a read-only re-render) + `per_property` stages
  // collapse into ONE screen. It IS the StaffTechnicalPanel marking surface
  // (which already marks one ACTIVE property at a time — grid + entrance + AUO —
  // via activeProp/switchProperty), fronted by the grid-status summary FOLDED
  // in from the retired GridStatusRollup (N complete / partial / not started).
  // The duplicate findings-pool confirm (evaluationFoldedEl) is retired here —
  // the report reads `fixtures[].status`, written by the per-AUO ✓ inside the
  // marking surface, NOT the folded findings confirm. `answers` write-shape is
  // unchanged.
  // LINK-VISIBLE candidates (owner ruling 2026-07-03 — "a correction must
  // propagate"): a candidate whose stamped propertyId FK was UNLINKED at Intake
  // (fell out of a NON-EMPTY live projects.propertyRefs[]) is dropped from
  // every link-derived read on this page — the grid summary and the stage-state
  // derivation below, matching the Evaluate candidate list's own drop
  // (StaffTechnicalPanel, same isCandidateLinkVisible rule — one helper, zero
  // drift). An EMPTY refs list is NO link data and never hides — real prod
  // projects (VP678) stamp candidates via a write lane that never maintains
  // propertyRefs[] (VP678 rule in candidatePropertyJoin). Read-side only: the
  // intake doc keeps the slot, so re-linking the property restores it.
  const l1aVisibleCands =
    l1aCands == null
      ? null
      : l1aCands.filter((c) =>
          isCandidateLinkVisible(c, liveMatch?.propertyRefs?.map((r) => r.propertyId) ?? []),
        );
  const evalGridSum: GridSummary | null =
    l1aVisibleCands != null ? gridSummary(l1aVisibleCands, l1aRecommendedId) : null;
  const perPropertyEvalEl = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* FOLDED grid-status summary (was the standalone Grids & charts stage). */}
      {evalGridSum && l1aVisibleCands && l1aVisibleCands.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--s-2)',
            flexWrap: 'wrap',
            padding: 'var(--s-2) var(--s-3)',
            borderRadius: 'var(--r-sm)',
            border: '1px solid var(--rule)',
            background: 'var(--surface, #fff)',
            fontSize: 12.5,
          }}
        >
          <strong style={{ color: 'var(--ink)' }}>Grid analysis</strong>
          <Tag kind="good">{evalGridSum.complete} complete</Tag>
          {evalGridSum.partial > 0 && <Tag kind="warn">{evalGridSum.partial} partial</Tag>}
          {evalGridSum.notStarted > 0 && (
            <Tag kind="tag">{evalGridSum.notStarted} not started</Tag>
          )}
          <span style={{ color: 'var(--ink-mute)' }}>
            {evalGridSum.isComparison
              ? `→ the report compares the ${evalGridSum.proceedable} ready candidates`
              : evalGridSum.readyToGenerate
                ? '→ 1 ready — grid a second for a comparison'
                : '→ grid a property (boundary + entrance) to make it report-ready'}
          </span>
        </div>
      )}
      {/* DECLUTTER 2026-07-02 (owner sign-off): the green instructional callout
          ("Evaluate one property fully…") and the standalone "Full-screen
          evaluation workspace:" links row are REMOVED. The per-property
          "Open workspace →" deep-links now live inside each candidate row in the
          merged Candidate-properties list (StaffTechnicalPanel), so there is ONE
          place to select a property AND open its workspace. */}
      {staffTechnicalEl}
      {/* RETIRED 2026-07-01 (owner sign-off): the folded findings-pool confirm
          (evaluationFoldedEl / findingsStore.confirmFinding) is removed from the
          Evaluate surface. It wrote a client-preview `findings/{…}.status` store
          the DEPLOYED report never reads — a second, non-authoritative confirm.
          The ONE confirm that matters is the per-AUO ✓ inside the marking
          surface above, which writes `fixtures[].status` (what the report reads).
          The findingsStore MODULE is intentionally NOT deleted — other tiers may
          still use it; only this L1A surface stops rendering the duplicate. */}
    </div>
  );

  // L1A Comparative body — the REAL §C cross-property comparison (2026-07-03,
  // replaces the dashed placeholder). ComparativePanel resolves Forge's
  // `resolveComparativeForProject` callable (fixture-gated in dev via
  // `?cmpFixture=` until the callable deploys), renders the traffic-light
  // table (stacked cards ≤860px), and mounts the SHARED
  // RecommendedPropertyPicker — the SAME real control the Verdict screen
  // uses, one `answers.recommended_property_id` write path.
  const comparativeEl = (
    <ProjectErrorBoundary surface="Comparative">
      <ComparativePanel
        clientId={resolvedClientId}
        projectId={p.pid}
        intakeTier={intakeTier}
        actor={{ uid: user?.uid ?? null, via: 'staff' }}
        onMsg={(msg) => toast(msg)}
        onGoEvaluate={() => goL1ATab('evaluate')}
        onRecommendationChanged={setL1aRecommendedId}
      />
    </ProjectErrorBoundary>
  );

  // ── L1A Intake screen (PASS 2) — the Open/Send intake actions relocated from
  // the old header + the candidate-linking panel. (The captured-brief summary
  // via loadIntake is a thin fast-follow; the candidate linking below is the
  // substantive intake surface.) The IntakeLinkSendBar modal stays mounted at
  // the L1A branch root so "Send" overlays from this tab.
  const intakeScreenEl = (
    <>
      <Box title="Intake form" note="Capture or send the client's brief">
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            padding: '10px 12px',
            borderRadius: 'var(--r-sm)',
            background: 'var(--accent-wash)',
            border: '1px solid var(--accent-soft, var(--rule))',
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--ink-soft)',
            marginBottom: 12,
          }}
        >
          <span aria-hidden style={{ color: 'var(--accent)', fontSize: 14 }}>✦</span>
          <p style={{ margin: 0 }}>
            Send the intake form over the client's best channel (WhatsApp-first, email
            fallback), or open it yourself to fill on a call. These actions live here in the
            stage — not in the page header.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {liveMatch?.clientId ? (
            <Link
              to={`/intake?tier=${encodeURIComponent(p.tier)}&client=${encodeURIComponent(liveMatch.clientId)}&pid=${encodeURIComponent(p.pid)}`}
            >
              <Btn3 intent="ink" size="sm">
                Open intake form
              </Btn3>
            </Link>
          ) : (
            <Btn3
              intent="ghost"
              size="sm"
              disabled
              title="Link a client to this project first (Edit project → re-link), then the form can be opened."
            >
              Open intake form
            </Btn3>
          )}
          {liveMatch?.clientId ? (
            <Btn3 intent="grn" size="sm" onClick={() => setShowSendLink(true)}>
              Send intake form
            </Btn3>
          ) : (
            <Btn3
              intent="ghost"
              size="sm"
              disabled
              title="Link a client to this project first, then the intake link can be sent."
            >
              Send intake form
            </Btn3>
          )}
        </div>
      </Box>
      {propertyLinkingEl}
      {linkedFamilyEl}
      {linkedContactsEl}
    </>
  );

  // ── L1A Intake-gate screen (PASS 2) — ONLY the `advance` money-gate, lifted
  // from the payments block. This is where Fee/Paid/Due workflow context now
  // lives. The full gate-by-gate payments panel stays in Settings (MoreDrawer
  // for now). Clearing/overriding here flips the gate everywhere (all read the
  // same gateState/clearedGateIds).
  const advanceGate = m.gates.find((g) => g.id === 'advance');
  const intakeGateScreenEl = (
    <ProjectErrorBoundary surface="Intake gate">
      <Box
        title="Gate checklist"
        note={
          effectiveMode === 'work_first'
            ? 'Work-first — advance tracked, NOT enforced (does not block work)'
            : 'The advance must clear before Evaluate unlocks'
        }
      >
        {advanceGate ? (
          <Card flat className="row-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <b>{money(advanceGate.amountINR, p.currency ?? 'INR')}</b>
              <span style={{ fontSize: 13, color: 'var(--ink-soft)' }}>{advanceGate.label}</span>
              <span style={{ marginLeft: 'auto' }}>
                {advanceGate.cleared ? (
                  <Tag>cleared</Tag>
                ) : effectiveMode === 'work_first' ? (
                  <Tag kind="warn">due · tracked (work-first)</Tag>
                ) : (
                  <Tag kind="warn">due</Tag>
                )}
              </span>
            </div>
            {!advanceGate.cleared && canOverrideGate(role) && (
              <GateOverridePanel
                projectId={p.pid}
                gateKind="money"
                gateId={advanceGate.id}
                gateLabel={advanceGate.label}
                valueINR={p.valueINR}
                onMsg={(msg) => toast(msg)}
              />
            )}
          </Card>
        ) : (
          <EmptyNote title="No advance gate">
            This tier defines no advance money gate.
          </EmptyNote>
        )}
      </Box>
      <Box title="Money picture" note="Fee · Paid · Due — the full gate-by-gate breakdown lives in Settings">
        <div className="l1a-screen">
          <div className="kv">
            <span className="k">Fee</span>
            <b>{money(m.valueINR, p.currency ?? 'INR')}</b>
          </div>
          <div className="kv">
            <span className="k">Paid</span>
            <b>{money(m.paidINR, p.currency ?? 'INR')}</b>
          </div>
          <div className="kv">
            <span className="k">Due</span>
            <b>{money(m.dueINR, p.currency ?? 'INR')}</b>
          </div>
        </div>
      </Box>
    </ProjectErrorBoundary>
  );

  // ── L1A tabbed shell ────────────────────────────────────────────────────
  // The merged stage-bar + one flat screen at a time. PASS 4 — the status feed
  // is now the REAL deriveL1AStageStates projection (was an all-`todo` stub):
  // every bar dot, screen pill, the Overview at-a-glance, and the default tab
  // read this one derivation. ownerReviewed/closingCallDone are passed false for
  // now (pass 5 wires the project-doc fields + the explicit sign-off controls).
  const l1aStageViews = deriveL1AStageStates({
    // LINK-VISIBLE candidates only (owner ruling 2026-07-03): an unlink
    // correction at Intake drops the candidate from the stage derivation too —
    // the dots can't stay green off a candidate the Evaluate list no longer
    // shows.
    candidates: l1aVisibleCands,
    recommendedId: l1aRecommendedId,
    reportStatus: l1aReportStatus,
    loading: isL1ATier && !!resolvedClientId && l1aCands == null,
    propertyRefs: liveMatch?.propertyRefs ?? [],
    advanceCleared: p.clearedGateIds.includes('advance'),
    intakeReceived: (l1aVisibleCands?.length ?? 0) > 0,
    // PASS 5 — the real project-doc booleans (default false on pre-field docs).
    // Owner-review + Closing-call dots now reflect the sign-off / mark-done.
    ownerReviewed: liveMatch?.ownerReviewed ?? false,
    closingCallDone: liveMatch?.closingCallDone ?? false,
    // Astro-Vastu confirm tick (2026-07-03) — flips the astro_vastu chip to ✓.
    // SUBJECT-GATED (owner bug #5): a confirmation with no remaining astro
    // subject is void — the chip must not stay green after total link removal.
    astroConfirmed: !!effectiveAstroConfirmation,
    // Compare tick (owner ruling 2026-07-03): a SAVED recommended property =
    // the comparison is done. Loaded with the intake read above and kept LIVE
    // by the picker's onChanged echo (Compare tab + Verdict screen both wire
    // setL1aRecommendedId), so the chip flips without a reload.
    recommendationSet: !!l1aRecommendedId,
    deliveredAt: null,
  });
  const l1aStatusByKey: Record<string, StageStatus> = Object.fromEntries(
    l1aStageViews.map((v) => [v.key, toBarStatus(v.state)]),
  );
  // `rawTab` is read once, up with the candidate-intake effect (it doubles as
  // that effect's tab-activation re-fetch key).
  // resolveL1ATabKey aliases retired keys (?tab=grids_charts / ?tab=per_property)
  // → `evaluate` so old bookmarks/deep-links don't 404 or silently reset.
  const resolvedTab = isL1ATier ? resolveL1ATabKey(rawTab) : null;
  const activeL1ATab: L1ATabKey = resolvedTab ?? defaultL1ATab(l1aStatusByKey);

  // Screen registry — the swap target. Built once in render scope; the selector
  // renders only L1A_SCREENS[activeL1ATab]. Every one of the 14 tabs is wired to
  // real content below (no placeholders remain after pass 5).
  const L1A_SCREENS = {} as Record<L1ATabKey, ReactNode>;
  L1A_SCREENS.overview = (
    <OverviewScreen
      clientName={clientName}
      vp={p.vp}
      tier={tpl.tier}
      assigneeName={p.assigneeName}
      candidateCount={liveMatch?.propertyRefs?.length ?? 0}
      money={m}
      currency={p.currency}
      statusByKey={l1aStatusByKey}
      onGo={goL1ATab}
    />
  );

  // Each tab's screen, wrapped flat in the StageScreen frame (no accordion, no
  // nesting). Status pills come from the real deriveL1AStageStates projection
  // (l1aStatusByKey); meta tabs (Settings) carry no pill.
  const l1aScreen = (
    key: L1ATabKey,
    title: string,
    sub: string,
    inner: ReactNode,
  ): ReactNode => (
    <StageScreen
      title={title}
      tabKey={key}
      status={l1aStatusByKey[key]}
      sub={sub}
      testId={`stage-screen-${key}`}
    >
      {inner}
    </StageScreen>
  );
  L1A_SCREENS.intake = l1aScreen('intake', 'Intake', 'Client brief that feeds the report', intakeScreenEl);
  L1A_SCREENS.intake_gate = l1aScreen('intake_gate', 'Intake gate', 'Checks that must pass before evaluation begins', intakeGateScreenEl);
  // MERGED 2026-07-01 (owner sign-off) — the old `grids_charts` (a read-only
  // GridStatusRollup re-render) + `per_property` screens collapse into ONE
  // `evaluate` screen. The grid-status summary is folded into the Evaluate
  // header (perPropertyEvalEl); the standalone GridStatusRollup screen is gone.
  // Wording per owner ruling 2026-07-03 (matches the Compare tab): AUOs are NOT
  // implied as required — a vacant plot has none; boundary + entrance alone is
  // a complete candidate.
  L1A_SCREENS.evaluate = l1aScreen(
    'evaluate',
    'Evaluate',
    'Grid → entrance, per candidate — mark AUOs where the property has them — then confirm each ruling',
    perPropertyEvalEl,
  );
  // Astro-Vastu confirm view + WRITE (mockup v2; owner ruling 2026-07-03).
  // Reads the bedrooms marked in Per-property + checks each family member's
  // chart (Forge's resolveAstroVastuForProject callable); "Confirm astro
  // inputs" is the staff tick — it snapshots the rendered resolve into
  // `astro_vastu_confirmation` on the project doc (lib/astroVastuConfirm),
  // which flips the stage chip above to done. Sits AFTER Per-property,
  // BEFORE Comparative.
  L1A_SCREENS.astro_vastu = l1aScreen(
    'astro_vastu',
    'Astro-Vastu',
    'Marked bedrooms (from Evaluate) → checked against each family member’s chart',
    <ProjectErrorBoundary surface="Astro-Vastu">
      <AstroVastuScreen
        projectId={p.pid}
        refreshKey={linkRefreshNonce}
        // SUBJECT-GATED (owner bug #5): null when no astro subject remains —
        // the "Confirmed by …" banner drops and the screen shows its plain
        // link-a-group guidance instead of a confirmation of a removed subject.
        confirmation={effectiveAstroConfirmation}
        actor={{ uid: user?.uid ?? null, name: actorName }}
        mayCommit={mayCommit}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>,
  );
  L1A_SCREENS.comparative = l1aScreen('comparative', 'Comparative', '§C cross-property comparison', comparativeEl);
  L1A_SCREENS.verdict = l1aScreen('verdict', 'Verdict', 'The §D recommendation — printed on the report', verdictFieldFlatEl);
  L1A_SCREENS.rendered = l1aScreen('rendered', 'Report render', 'Generate → approve → deliver', reportStepEl);
  L1A_SCREENS.post_delivery_followup = l1aScreen('post_delivery_followup', 'Post-delivery follow-up', 'Finalised property + next-level intent', followUpEl);

  // PASS 5 — the four remaining screens: two thin glue screens (Owner review,
  // Closing call) driven by the new project-doc booleans, one read-only Delivered
  // record, and the flattened Settings (the old "Project settings & more" drawer
  // contents + Edit project — no drawer; it lives ONLY here now).
  L1A_SCREENS.owner_review = l1aScreen(
    'owner_review',
    'Owner review',
    'Owner signs off the draft before it renders',
    <ProjectErrorBoundary surface="Owner review">
      <OwnerReviewScreen
        pid={p.pid}
        ownerReviewed={liveMatch?.ownerReviewed ?? false}
        reportStatus={l1aReportStatus}
        mayCommit={mayCommit}
        actorName={actorName}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>,
  );
  L1A_SCREENS.delivered = l1aScreen(
    'delivered',
    'Delivered',
    'The client has the report',
    <ProjectErrorBoundary surface="Delivered">
      <DeliveredScreen
        pid={p.pid}
        clientId={resolvedClientId}
        deliverableTier={l1aDeliverableTier}
        latest={l1aLatest}
        status={l1aReportStatus}
        onMsg={(msg) => toast(msg)}
        onGo={goL1ATab}
      />
    </ProjectErrorBoundary>,
  );
  L1A_SCREENS.closing_call = l1aScreen(
    'closing_call',
    'Closing call',
    'Walk the client through the recommendation',
    <ProjectErrorBoundary surface="Closing call">
      <ClosingCallScreen
        pid={p.pid}
        done={liveMatch?.closingCallDone ?? false}
        delivered={l1aReportStatus === 'delivered'}
        mayCommit={mayCommit}
        actorName={actorName}
        onMsg={(msg) => toast(msg)}
      />
    </ProjectErrorBoundary>,
  );
  L1A_SCREENS.settings = l1aScreen(
    'settings',
    'Settings',
    'Everything administrative in one place',
    <>
      <ProjectEditPanel
        project={p}
        clientName={clientName}
        archived={liveMatch?.archived === true}
        actorName={actorName}
        allProjects={live}
        onMsg={(msg) => toast(msg)}
      />
      {paymentsEl}
      {participantsEl}
      {accessEl}
      {filesEl}
    </>,
  );

  return (
    <>
    <div className="project-work-grid">
      {/* ── Main content column ── */}
      <div className="project-work-col">
      {/* ── Identity is now in the top chrome bar (.phead), published via
          usePageChrome() above — no separate working-area identity strip. The
          work surface starts straight at the action row below (owner ask
          2026-06-28). The money chip lives in the published `meta` slot, keeping
          data-testid="l1a-money-chip". ── */}

      {/* ── Action row — non-L1A only. For L1A, Edit project moves to the
          header (→ Settings tab) and Open/Send intake relocate into the Intake
          screen (pass 2); the top row is suppressed on the L1A path. ── */}
      {!isL1ATier && (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => setShowEdit((s) => !s)}
        >
          {showEdit ? 'Close edit' : 'Edit project'}
        </Btn3>
        {/* Open the intake FORM for this project — the service (tier) and the
            client come across automatically; no picker, no URL editing.
            (owner ruling 2026-06-16 — a form is opened from a lead/project.)
            Needs a linked client to write the intake doc. */}
        {liveMatch?.clientId ? (
          <Link
            to={`/intake?tier=${encodeURIComponent(p.tier)}&client=${encodeURIComponent(liveMatch.clientId)}&pid=${encodeURIComponent(p.pid)}`}
          >
            <Btn3 intent="ink" size="sm">
              Open intake form
            </Btn3>
          </Link>
        ) : (
          <Btn3
            intent="ghost"
            size="sm"
            disabled
            title="Link a client to this project first (Edit project → re-link), then the form can be opened."
          >
            Open intake form
          </Btn3>
        )}
        {/* M5-S2 — mint + send the intake link to the client over their best
            channel (AD-020: WhatsApp-first, email fallback). Needs a linked
            client to read the contact channels + write the link doc. */}
        {liveMatch?.clientId ? (
          <Btn3
            intent="grn"
            size="sm"
            onClick={() => setShowSendLink(true)}
          >
            Send intake form
          </Btn3>
        ) : (
          <Btn3
            intent="ghost"
            size="sm"
            disabled
            title="Link a client to this project first, then the intake link can be sent."
          >
            Send intake form
          </Btn3>
        )}
      </div>
      )}

      {/* ── L1A: the merged StageTabBar (Overview · 11 stages · Settings) takes
          the place of the demoted identity Box. Status dots are a stub (all
          todo) in pass 1; the real deriveL1AStageStates feed lands in pass 4. */}
      {isL1ATier && (
        <StageTabBar
          items={L1A_TAB_ITEMS}
          activeKey={activeL1ATab}
          statusByKey={l1aStatusByKey}
          onSelect={goL1ATab}
        />
      )}

      {/* ── Demoted identity Box → StageRail + tier extras + (non-L1A) tabs.
          Identity (Client·VP·Tier·money·assignee) now lives in the sticky
          header above; this card keeps the stage rail and the workflow tabs.
          Non-L1A only — L1A is the StageTabBar above. */}
      {!isL1ATier && (
      <Box title={projectLabel(p.projectName, p.vp, tpl.tier)} note={canonicalServiceNameFor(tpl.tier) ?? tpl.displayName}>
        <StageRail stages={railOf(p)} />
        {/* L1C upsell banner (L1C_L2C_AVBP_INTEGRATION §4.2) — the in-product
            upsell. L1C is a READ-ONLY review (no redline, no architect contact);
            to redline + coordinate, the client upgrades to L2C, where the ₹11,000
            is credited in full within 60 days (the Setu credit, computed on the
            L2C quote). The credit applies automatically when Sales raises the L2C
            quote for this party. */}
        {bareTierCode === 'L1C' && (
          <div
            data-testid="l1c-upsell-banner"
            style={{
              marginTop: 12,
              fontSize: 12.5,
              color: 'var(--accent-deep, #1e40af)',
              background: 'var(--accent-wash, #eff6ff)',
              borderLeft: '3px solid var(--accent, #2563eb)',
              borderRadius: '0 var(--r-sm) var(--r-sm) 0',
              padding: '10px 14px',
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: 1, minWidth: 200 }}>
              <b>Read-only review</b> — no drawing markup, no architect contact. To redline &amp;
              coordinate the fixes with your architect, upgrade to{' '}
              <b>The Integration Path · Samanvaya (L2C)</b>. The ₹11,000 is credited in full within
              60 days.
            </span>
            {/* BREAK-1 fix (linkage audit 2026-07-03): carry THIS project into
                /quotes so the composer opens pre-filled (same nav-state hook
                as the lead's "Create quote"). Was a bare link → dead end. */}
            <Link to="/quotes" state={{ newQuoteProjectId: p.pid }}>
              <Btn3 intent="ink" size="sm">
                Raise L2C quote (Setu credit applies)
              </Btn3>
            </Link>
          </div>
        )}
        {/* Workflow tabs — suppressed for L1A: the 5-step flow rail (below)
            is the navigation. Every other tier keeps the tab bar. */}
        {!isL1ATier && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            margin: '14px 0 4px',
          }}
        >
          {tabs.map((tb) => {
            const surface =
              tb.key === 'marma' ? 'marma' : tb.key === 'devta' ? 'devta' : null;
            const locked =
              surface != null &&
              !isSurfaceUnlocked(tpl, surface, p.activeStageKey);
            return (
              <button
                key={tb.key}
                className={`pill ${tab === tb.key ? 'on' : ''}`}
                disabled={tb.laterSprint || locked}
                title={
                  locked
                    ? `${tb.label} — unlocks later in this tier`
                    : tb.laterSprint
                      ? `${tb.label} — surface lands in a later sprint (part of ${tpl.tier})`
                      : tb.label
                }
                onClick={() => setTab(tb.key)}
              >
                {tb.label}
                {locked ? ' 🔒' : ''}
              </button>
            );
          })}
        </div>
        )}
      </Box>
      )}

      {showEdit && (
        <ProjectEditPanel
          project={p}
          clientName={clientName}
          archived={liveMatch?.archived === true}
          actorName={actorName}
          allProjects={live}
          onMsg={(m) => toast(m)}
        />
      )}

      {/* M5-S2 — Send intake form modal (AD-020 smart channel). */}
      {showSendLink && liveMatch?.clientId && (
        <IntakeLinkSendBar
          cid={liveMatch.clientId}
          pid={p.pid}
          onClose={() => setShowSendLink(false)}
        />
      )}

      {/* Work / AUO canvas frame — non-L1A only. L1A replaces the tab-switched
          canvas + the flat panel stack with the <L1AFlow> 5-step accordion. */}
      {!isL1ATier && (
      <ProjectErrorBoundary surface="Work canvas / tab content">
      <Box
        title="Work canvas"
        note={mayCommit ? 'You hold commit rights on this project' : 'Read-only — you are not the assignee'}
      >
        {tab === 'messages' ? (
          <EmptyNote title="The conversation is the work surface">
            Findings, intake, photo-tags and recommendations all flow from
            the Project Conversation — visible in the panel on the right.
          </EmptyNote>
        ) : tpl.tier === 'L1A' && tab === 'report' ? (
          <EmptyNote title="Report render & delivery">
            Generate the {tpl.tier} report, review the draft, then Approve &amp; Deliver in the
            <b> Composer &amp; delivery</b> panel below. It reads the gridded property data + the
            confirmed findings; a comparison includes whichever candidates are grid-ready.
          </EmptyNote>
        ) : tpl.tier === 'L1A' && tab === 'intake' ? (
          <EmptyNote title="Intake">
            The client intake (people, goals, concerns, candidate properties) feeds the report.
            Use the intake link / summary above; link each candidate property below.
          </EmptyNote>
        ) : (
          <EmptyNote title={`${tab} canvas`}>
            The {tab} working canvas (AUO marking, drawings, overlays) lands
            with its dedicated sprint. It is a real part of {tpl.tier} —
            resolved from the locked engagementTemplate, shown here so the
            track is walkable end-to-end.
          </EmptyNote>
        )}
      </Box>
      </ProjectErrorBoundary>
      )}

      {/* THE WORK FLOW. L1A → the 5-step accordion (one open at a time); every
          other tier → the legacy flat panel stack (byte-identical). The reused
          panels are the SAME elements (built once above), just placed differently. */}
      {isL1ATier ? (
        // BUILD PASS 1 — the merged stage-bar + ONE flat screen at a time. The
        // active tab's screen renders here (Overview is real; other tabs are
        // placeholders this pass). <L1AFlow> is retired from the L1A path (its
        // import is kept until pass 2 per the build plan).
        <ProjectErrorBoundary surface="Stage screen">
          <div className="l1a-stage-screens">{L1A_SCREENS[activeL1ATab]}</div>
        </ProjectErrorBoundary>
      ) : (
        <>
          {propertyLinkingEl}
          {linkedFamilyEl}
          {linkedContactsEl}
          {staffTechnicalEl}
          {evaluationEl}
          {reportComposerShellEl}
          {composerDeliveryEl}
        </>
      )}

      {/* The non-flow panels (clean-flow §6). PASS 5 — L1A no longer renders a
          bottom "Project settings & more" drawer: its contents (Payments ·
          Participants · Access · Files + Edit project) now live ONLY under the
          Settings tab screen. Every other tier keeps its inline flat stack
          (byte-identical). */}
      {!isL1ATier && (
        <>
          {filesEl}
          {participantsEl}
          {accessEl}
          {paymentsEl}
          {followUpEl}
        </>
      )}

      </div>{/* end main content column */}

      {/* ── Right column — project conversation thread (320px, sticky on
          desktop; display:none ≤860px, where the FAB + sheet take over). All
          layout now lives in `.project-comms-col` (components.css) so the media
          query can restack it — desktop computed styles are byte-identical to
          the former inline block. ── */}
      <div className="project-comms-col">
        <ProjectErrorBoundary surface="Project conversation">
        <ProjectThreadPanel
          projectId={p.pid}
          clientId={liveMatch?.clientId}
        />
        </ProjectErrorBoundary>
      </div>
    </div>

    {/* ── Mobile-only Conversation entry (≤860px). The persistent right column
        is hidden on phones; this green chat FAB opens a full-height sheet that
        mounts the SAME <ProjectThreadPanel>. Hidden on desktop via CSS
        (display:none), so it never renders over the visible column. ── */}
    <button
      type="button"
      className="project-chat-fab"
      onClick={() => setCommsSheetOpen(true)}
      aria-haspopup="dialog"
      aria-expanded={commsSheetOpen}
      aria-label={`Open conversation${clientName && clientName !== '—' ? ` with ${clientName}` : ''}`}
      title="Conversation"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
      </svg>
    </button>

    {commsSheetOpen && (
      <ProjectCommsSheet
        projectId={p.pid}
        clientId={liveMatch?.clientId}
        clientName={clientName}
        onClose={() => setCommsSheetOpen(false)}
      />
    )}
    </>
  );
}

/* ════════════════════ P1 — inline project edit + archive ════════════════════
 *
 *  595 imported projects all carry the placeholder tier
 *  "(legacy import — confirm tier)". Khilendra corrects them here. Writes
 *  go through projectStore.updateProject / archiveProject — rules already
 *  allow isTeam() full update on projects/; hard delete is impossible
 *  (delete:false) so "Archive" is a reversible `archived:true` flag.
 *  Confirm-gated, two-step (mirrors the AppShell sign-out confirm).
 */
const editInput: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function EditLbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '10px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

function ProjectEditPanel({
  project,
  clientName,
  archived,
  actorName,
  allProjects,
  onMsg,
}: {
  project: DemoProject;
  /** Canonical client name (resolved from the clients/{id} master). */
  clientName: string;
  archived: boolean;
  actorName: string;
  /**
   * The already-streamed live project list — folded (no extra read) by
   * `validateVpEdit` to guard VP-number uniqueness on Save.
   */
  allProjects: LiveProject[];
  onMsg: (m: string) => void;
}) {
  const tpl = templateOf(project);
  // The project's own name — editable. Empty for a pre-field project doc.
  const [projectName, setProjectName] = useState(project.projectName ?? '');
  // The human VP number — editable label (design 2026-07-17 §3). Rewrites the
  // `vp` field only; the doc id (every reference's key) is untouched.
  const [vp, setVp] = useState(project.vp ?? '');
  // VP-uniqueness rejection, surfaced inline (a11y: survives the toast timeout
  // and is programmatically linked to the input via aria-describedby). Set on
  // a Save-time validator rejection; cleared as soon as the field is edited.
  const [vpError, setVpError] = useState<string | null>(null);
  const vpErrorId = `vp-error-${project.pid}`;
  const [tier, setTier] = useState<Tier>(project.tier as Tier);
  const [valueINR, setValueINR] = useState(String(project.valueINR));
  const [assigneeName, setAssigneeName] = useState(project.assigneeName);
  const [activeStageKey, setActiveStageKey] = useState(
    project.activeStageKey,
  );
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (busy) return;
    // VP-number uniqueness guard — validate BEFORE any write, against the
    // already-loaded live list (no extra read). Block Save on a collision.
    const vpCheck = validateVpEdit(vp, allProjects, project.pid);
    if (!vpCheck.ok) {
      const msg = vpCheck.error ?? 'That VP number cannot be used.';
      setVpError(msg);
      onMsg(msg);
      return;
    }
    setVpError(null);
    setBusy(true);
    try {
      const displayName =
        canonicalTierOptions().find((o) => o.tier === tier)?.displayName ??
        tpl.displayName;
      // VP relabel — only when the number actually changed. Writes the single
      // `vp` field + its own project_vp_relabel ledger entry (audit trail).
      const currentVp = (project.vp ?? '').trim().toUpperCase();
      if (vpCheck.value !== currentVp) {
        await relabelProjectVp(project.pid, vpCheck.value, project.vp, actorName);
      }
      // NOTE — `clientName` is NOT written here. The client name has ONE
      // canonical source: the clients/{id} master. Editing it on the
      // project doc only drifts the per-project cache (the C00464 bug).
      // Rename a client on Client Detail; re-link a project to a different
      // client via the Re-link tool on All Projects.
      await updateProject(
        project.pid,
        {
          projectName: projectName.trim(),
          tierCode: tier,
          tier: `${tier} · ${displayName}`,
          valueINR: Number(valueINR) || 0,
          assigneeName: assigneeName.trim(),
          activeStageKey: activeStageKey.trim(),
        },
        actorName,
      );
      onMsg(`Saved ${project.vp}.`);
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doArchive() {
    if (busy) return;
    setBusy(true);
    try {
      await archiveProject(project.pid, actorName, !archived);
      onMsg(`${project.vp} ${archived ? 'restored' : 'archived'}.`);
      setConfirmArchive(false);
    } catch (e) {
      onMsg(`Archive failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box
      title="Edit project"
      note="Project name, tier, value, assignee, stage"
    >
      {/* The project's OWN name — its identity, distinct from the client and
          the VP code. Blank degrades gracefully to `VP### · tier`. */}
      <EditLbl>Project name</EditLbl>
      <input
        value={projectName}
        onChange={(e) => setProjectName(e.target.value)}
        placeholder={projectLabel(null, project.vp, tpl.tier)}
        aria-label="Project name"
        style={editInput}
      />
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '4px 0 0' }}>
        The project's own name — e.g. "Residence — Vastu review". Distinct
        from the client and the {project.vp} code. Leave blank to show{' '}
        <b>{projectLabel(null, project.vp, tpl.tier)}</b>.
      </p>

      {/* The human VP number — an EDITABLE label (design 2026-07-17 §3). The
          project's internal reference never changes, so quotes, payments and
          messages stay linked; only this human-facing number is rewritten.
          Must stay unique across projects (guarded on Save). */}
      <EditLbl>VP number</EditLbl>
      <input
        value={vp}
        onChange={(e) => { setVp(e.target.value.toUpperCase()); if (vpError) setVpError(null); }}
        placeholder={project.vp}
        aria-label="VP number"
        aria-invalid={vpError ? true : undefined}
        aria-describedby={vpError ? vpErrorId : undefined}
        style={vpError ? { ...editInput, borderColor: 'var(--crit)' } : editInput}
      />
      {vpError && (
        <p
          id={vpErrorId}
          role="alert"
          style={{ fontSize: 12, color: 'var(--crit)', margin: '4px 0 0' }}
        >
          {vpError}
        </p>
      )}
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '4px 0 0' }}>
        The project's VP number — editable. Quotes, payments and messages stay
        linked when you change it. Must be unique across all projects.
      </p>

      <EditLbl>Client</EditLbl>
      <div
        style={{
          ...editInput,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--inset, #f6f7f9)',
          color: 'var(--ink)',
        }}
      >
        <b>{clientName}</b>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {project.clientNo}
        </span>
      </div>
      <p style={{ fontSize: 12, color: 'var(--ink-mute)', margin: '4px 0 0' }}>
        The client name comes from the client master record — edit it on the
        Client Detail screen. To move this project to a different client, use
        the Re-link tool on the All Projects list.
      </p>

      <EditLbl>Service tier (imports are placeholder — confirm)</EditLbl>
      <select
        value={tier}
        onChange={(e) => setTier(e.target.value as Tier)}
        style={{ ...editInput, minWidth: 260 }}
      >
        {canonicalTierOptions().map((o) => (
          <option key={o.tier} value={o.tier}>
            {o.tier} · {o.displayName}
          </option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 150 }}>
          <EditLbl>Value (₹)</EditLbl>
          <input
            value={valueINR}
            onChange={(e) =>
              setValueINR(e.target.value.replace(/[^\d]/g, ''))
            }
            style={editInput}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <EditLbl>Assignee</EditLbl>
          <input
            value={assigneeName}
            onChange={(e) => setAssigneeName(e.target.value)}
            style={editInput}
          />
        </div>
        <div style={{ flex: 1, minWidth: 150 }}>
          <EditLbl>Active stage key</EditLbl>
          <input
            value={activeStageKey}
            onChange={(e) => setActiveStageKey(e.target.value)}
            style={editInput}
          />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 16,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Btn3 intent="grn" size="sm" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save changes'}
        </Btn3>
        {!confirmArchive ? (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setConfirmArchive(true)}
            disabled={busy}
          >
            {archived ? 'Restore…' : 'Archive…'}
          </Btn3>
        ) : (
          <span
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--ink-soft)' }}>
              {archived
                ? 'Restore this project?'
                : 'Archive — hidden from the default list (reversible, never deleted).'}
            </span>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setConfirmArchive(false)}
            >
              Cancel
            </Btn3>
            <Btn3
              intent="ink"
              size="sm"
              onClick={doArchive}
              disabled={busy}
            >
              {archived ? 'Yes, restore' : 'Yes, archive'}
            </Btn3>
          </span>
        )}
      </div>
    </Box>
  );
}

/* ════════════════════ Per-project payment-mode override ════════════════════
 *
 *  owner directive 2026-05-19 — money-before-delivery is now a configurable
 *  policy. This panel shows the EFFECTIVE mode for the project, where it
 *  came from (project override vs org default), and (admin only) lets an
 *  admin set/clear a per-project override. Staff see it read-only — the
 *  policy is an admin decision (mirrors lead-identity edit being admin-only).
 *  Writes go through paymentMode.setProjectPaymentMode (isTeam updateDoc on
 *  projects/ — no rules change).
 */
function PaymentModePanel({
  pid,
  projectOverride,
  globalMode,
  effectiveMode,
  isAdmin,
  actorName,
  onMsg,
}: {
  pid: string;
  projectOverride: PaymentMode | null;
  globalMode: PaymentMode;
  effectiveMode: PaymentMode;
  isAdmin: boolean;
  actorName: string;
  onMsg: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function apply(next: PaymentMode) {
    if (busy || next === projectOverride) return;
    setBusy(true);
    try {
      await setProjectPaymentMode(pid, next, actorName);
      onMsg(
        `${pid} payment mode → ${paymentModeLabel(next)} (project override).`,
      );
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  // "Clear override" = write the current org default explicitly. (There is
  // no rules-safe field-delete here; setting it to the org default is the
  // honest, equivalent effect and is stated as such in the UI.)
  async function clearOverride() {
    if (busy) return;
    setBusy(true);
    try {
      await setProjectPaymentMode(pid, globalMode, actorName);
      onMsg(`${pid} now follows the org default (${paymentModeLabel(globalMode)}).`);
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card flat style={{ marginBottom: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <b style={{ fontSize: 13 }}>Payment mode</b>
        <Tag kind={effectiveMode === 'work_first' ? 'warn' : 'val'}>
          {paymentModeLabel(effectiveMode)}
        </Tag>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          {projectOverride
            ? 'project override'
            : `following org default (${paymentModeLabel(globalMode)})`}
        </span>
      </div>
      <p
        style={{
          fontSize: 12,
          color: 'var(--ink-soft)',
          margin: '6px 0 0',
          lineHeight: 1.5,
        }}
      >
        {effectiveMode === 'work_first'
          ? 'Work proceeds without waiting for payment. Amounts are still tracked and shown below — they do not block any stage. Curation/quality gates still apply.'
          : 'A stage cannot be entered until its money gate is paid (locked money-before-delivery).'}
      </p>
      {isAdmin ? (
        <div
          style={{
            display: 'flex',
            gap: 8,
            marginTop: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          {PAYMENT_MODE_OPTIONS.map((o) => (
            <Btn3
              key={o.mode}
              intent={projectOverride === o.mode ? 'ink' : 'ghost'}
              size="sm"
              onClick={() => apply(o.mode)}
              disabled={busy || projectOverride === o.mode}
              title={o.blurb}
            >
              {projectOverride === o.mode
                ? `✓ ${o.label}`
                : `Set: ${o.label}`}
            </Btn3>
          ))}
          {projectOverride && (
            <Btn3
              intent="ghost"
              size="sm"
              onClick={clearOverride}
              disabled={busy}
              title="Stop overriding — follow the org default"
            >
              Use org default
            </Btn3>
          )}
        </div>
      ) : (
        <p
          style={{
            fontSize: 12,
            color: 'var(--ink-mute)',
            marginTop: 10,
          }}
        >
          The payment mode is set by an admin.
        </p>
      )}
    </Card>
  );
}

/* ════════════════════ Files — real Storage upload + list ════════════════════
 *
 *  AVBP_FLOW_STATUS 2026-05-19 §C.3 — "without an upload control, files
 *  cannot enter the system". Wires the real upload (projectFiles.ts) into
 *  the team work surface. Storage + Firestore artefact paths are BOTH
 *  already permitted by the LIVE rules (no rules change). Team-only by the
 *  route guard (roles=['admin','staff']) AND by the rule (isTeam()).
 *  Confirm-gated archive (no hard delete — rules: delete:false), mirroring
 *  the ProjectEditPanel archive pattern.
 */
function FilesPanel({
  pid,
  files,
  actorUid,
  actorName,
  onMsg,
}: {
  pid: string;
  /** Streamed at the route level (one listener, shared with the floor
   *  tracer's "Choose from project files"). null = still loading. */
  files: ProjectFile[] | null;
  actorUid: string;
  actorName: string;
  onMsg: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    // reset the input so the same file can be re-picked after an error
    if (inputRef.current) inputRef.current.value = '';
    if (!f || busy) return;
    setBusy(true);
    try {
      await uploadProjectFile(pid, f, { uid: actorUid, name: actorName });
      onMsg(`Uploaded "${f.name}".`);
    } catch (err) {
      onMsg(`Upload failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doArchive(file: ProjectFile) {
    if (busy) return;
    setBusy(true);
    try {
      await archiveProjectFile(
        pid,
        file.fileId,
        actorName,
        !file.archived,
      );
      onMsg(
        `"${file.fileName}" ${file.archived ? 'restored' : 'archived'}.`,
      );
      setConfirmId(null);
    } catch (err) {
      onMsg(`Failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const visible = (files ?? []).filter((f) =>
    showArchived ? true : !f.archived,
  );
  const archivedCount = (files ?? []).filter((f) => f.archived).length;

  return (
    <Box
      title="Files"
      note="Floor plans · photos · PDFs · documents — stored on the project"
    >
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf,application/*"
          onChange={onPick}
          style={{ display: 'none' }}
          id="proj-file-input"
        />
        <Btn3
          intent="ink"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
        >
          {busy ? 'Uploading…' : '⬆ Upload a file'}
        </Btn3>
        <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
          Images, PDF and documents · up to 25 MB
        </span>
        {archivedCount > 0 && (
          <span style={{ marginLeft: 'auto' }}>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setShowArchived((s) => !s)}
            >
              {showArchived
                ? 'Hide archived'
                : `Show archived (${archivedCount})`}
            </Btn3>
          </span>
        )}
      </div>

      {files === null && (
        <EmptyNote title="Loading files…">
          Reading the project's files.
        </EmptyNote>
      )}

      {files !== null && visible.length === 0 && (
        <EmptyNote title="No files yet">
          Upload a floor plan, site photo, or PDF — it is stored on this
          project and listed here for the team.
        </EmptyNote>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visible.map((f) => (
          <Card key={f.fileId} flat className="row-card">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span aria-hidden style={{ fontSize: 15 }}>
                {f.kind === 'image'
                  ? '🖼'
                  : f.kind === 'pdf'
                    ? '📄'
                    : f.kind === 'doc'
                      ? '📃'
                      : '📎'}
              </span>
              <a
                href={f.downloadURL || undefined}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontWeight: 600,
                  fontSize: 13.5,
                  color: 'var(--ink)',
                  textDecoration: 'underline',
                  wordBreak: 'break-all',
                }}
              >
                {f.fileName}
              </a>
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                {humanSize(f.size)} · {f.uploaderName}
              </span>
              {f.archived && <Tag kind="warn">archived</Tag>}
              <span style={{ marginLeft: 'auto' }}>
                {confirmId === f.fileId ? (
                  <span
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      fontSize: 12,
                    }}
                  >
                    <span style={{ color: 'var(--ink-soft)' }}>
                      {f.archived
                        ? 'Restore this file?'
                        : 'Archive — hidden from the list (reversible, file kept).'}
                    </span>
                    <Btn3
                      intent="ghost"
                      size="sm"
                      onClick={() => setConfirmId(null)}
                    >
                      Cancel
                    </Btn3>
                    <Btn3
                      intent="ink"
                      size="sm"
                      onClick={() => doArchive(f)}
                      disabled={busy}
                    >
                      {f.archived ? 'Yes, restore' : 'Yes, archive'}
                    </Btn3>
                  </span>
                ) : (
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() => setConfirmId(f.fileId)}
                    disabled={busy}
                  >
                    {f.archived ? 'Restore…' : 'Archive…'}
                  </Btn3>
                )}
              </span>
            </div>
          </Card>
        ))}
      </div>
    </Box>
  );
}

/* ════════════════════ Task C — admin money-gate override ════════════════════
 *
 *  Scenario SC-058 (owner Gate-1 ruling SC-012/013/015/016 + OQ-011): an
 *  admin may override a money gate to release a deliverable before payment.
 *  EVERY override is recorded as an immutable money.gate.override.audit
 *  ledger entry AND triggers a staged payment-reminder mechanism.
 *
 *  This panel is admin-ONLY (canOverrideGate). It collects the mandatory
 *  reason and calls the recordGateOverride Cloud Function, which does all
 *  three writes server-side (gate clearance + audit entry + INERT reminder).
 *  Two-step confirm — mirrors the ProjectEditPanel archive pattern.
 *
 *  The reminder is created INERT (status 'armed_pending'); the panel says
 *  so — no client message is sent by this action.
 */
function GateOverridePanel({
  projectId,
  gateKind,
  gateId,
  gateLabel,
  valueINR,
  onMsg,
}: {
  projectId: string;
  gateKind: GateKind;
  gateId: string;
  gateLabel: string;
  valueINR: number;
  onMsg: (m: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [amountPaid, setAmountPaid] = useState('0');
  const [busy, setBusy] = useState(false);

  async function doOverride() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await recordGateOverride({
        projectId,
        gateKind,
        gateId,
        reason: reason.trim(),
        amountPaid: Number(amountPaid) || 0,
      });
      onMsg(
        `Override recorded · audit entry ${res.ledgerId.slice(0, 6)}… · ` +
          `₹${res.outstandingINR} outstanding · reminder seeded (inert).`,
      );
      setOpen(false);
      setReason('');
    } catch (e) {
      onMsg(`Override failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div style={{ marginTop: 8 }}>
        <Btn3 intent="ghost" size="sm" onClick={() => setOpen(true)}>
          Admin override…
        </Btn3>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: 10,
        padding: 12,
        border: '1px solid var(--rule)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <p style={{ fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.55, margin: '0 0 8px' }}>
        Override <b>{gateLabel}</b> — release without payment. This is
        permanently recorded as an audit entry and starts a staged payment
        reminder. The reminder is created <b>inert</b> (it does not send
        until armed by the owner).
      </p>
      <EditLbl>Reason (required — permanently audited)</EditLbl>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="e.g. long-duration engagement, balance on the Day-44 review"
        style={{ ...editInput, resize: 'vertical' }}
      />
      <EditLbl>Amount already paid (₹) — for the outstanding-balance record</EditLbl>
      <input
        value={amountPaid}
        onChange={(e) => setAmountPaid(e.target.value.replace(/[^\d]/g, ''))}
        style={{ ...editInput, maxWidth: 200 }}
      />
      <div style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 4 }}>
        Project value on record: ₹{Number(valueINR) || 0}.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Btn3
          intent="ink"
          size="sm"
          onClick={doOverride}
          disabled={busy || reason.trim().length < 4}
        >
          {busy ? 'Recording…' : 'Confirm override + start reminder'}
        </Btn3>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => setOpen(false)}
          disabled={busy}
        >
          Cancel
        </Btn3>
      </div>
    </div>
  );
}

/* ════════════════════ Phase-2 Slice 2 — Composer + delivery ════════════════════
 *
 *  Wires the "Generate Draft" button (the button produces a DRAFT, not a final
 *  — renamed §FIX-3) to the Slice-1 composeReport CF, then the Slice-2
 *  approve → deliver seam:
 *
 *    Generate / Regenerate Draft → composeReport (ungated DRAFT). Any staff/admin
 *                             (SC-037 — generation is not assignee-gated).
 *    Approve & Deliver      → approveReport (draft→approved) THEN deliverReport
 *                             (server re-checks money-before-delivery + curation;
 *                             a block surfaces the server's reason verbatim).
 *                             Visible to admin/assignee (curation rule).
 *
 *  Money + curation are NEVER decided here — the server (recheckGate delivery
 *  verdict / gateState.moneyPaid) is authoritative. We only invoke + display.
 *  Report status (draft / approved / delivered) is streamed from
 *  clients/{cid}/reports (team-read) so the panel reflects live state.
 * ──────────────────────────────────────────────────────────────────────── */
function ComposerDeliveryPanel({
  pid,
  clientId,
  tierCode,
  role,
  mayCommit,
  onMsg,
}: {
  pid: string;
  clientId: string | null;
  tierCode: string;
  role: Role | null;
  mayCommit: boolean;
  onMsg: (m: string) => void;
}) {
  const deliverableTier = toDeliverableTier(tierCode);
  const mayGenerate = canGenerateReport(role);

  const [reports, setReports] = useState<ReportDoc[]>([]);
  const [busy, setBusy] = useState<null | 'generate' | 'deliver'>(null);
  const [blockReason, setBlockReason] = useState<string | null>(null);

  // Stream the project's report docs (team-read). Skip when no client link or
  // the tier is not wired — the panel still renders an explanatory state.
  useEffect(() => {
    if (!clientId || !deliverableTier) {
      setReports([]);
      return;
    }
    const unsub = listenProjectReports(clientId, pid, setReports, () => {});
    return unsub;
  }, [clientId, pid, deliverableTier]);

  const latest = deliverableTier ? latestReportForTier(reports, deliverableTier) : null;
  const status: ReportStatus | null = latest ? reportStatusOf(latest) : null;

  // Step 3 (AD-048 / revised ADR-4) — candidate grid-analysis subset gating. Read
  // the same intake the composer reads (project-scoped) and roll up which
  // candidates are PROCEED-ABLE (boundary + ≥1 entrance; AUOs optional) → which
  // the report will compare. Re-loaded after a generate (reports change) so a
  // fresh save is reflected. L1A only; null elsewhere → no gating.
  const [grid, setGrid] = useState<GridSummary | null>(null);
  // clean-flow spec §4.2 — a fetch FAILURE must be a DISTINCT state, not the same
  // null as "no client / not L1A". On error we keep gating PERMISSIVE (never a
  // false block) but record it so the cause is visible, not silently swallowed.
  const [gridError, setGridError] = useState(false);
  useEffect(() => {
    if (!clientId || deliverableTier !== 'L1A') {
      setGrid(null);
      setGridError(false);
      return;
    }
    let alive = true;
    setGridError(false);
    (async () => {
      try {
        // Read the SAME project-scoped intake the staff flow writes (L1A_R1),
        // not the default L2B_R1 — else the generate-gate reads an empty/foreign
        // doc and wrongly reports 0 proceed-able candidates (disabling Generate).
        const r = await loadPropertiesIntake({ clientId, projectId: pid }, `${deliverableTier}_R1`);
        // READ-TIME grid hydration: reflect each candidate's linked PROPERTY grid
        // before the generate-gate summary is computed, so a gridded property
        // unblocks Generate even though the candidate's own floors are empty.
        const hydrated = await hydrateCandidatesWithPropertyGrids(r.form.properties);
        if (alive) {
          setGrid(gridSummary(hydrated, r.recommendedPropertyId));
          setGridError(false);
        }
      } catch {
        if (alive) {
          setGrid(null);
          setGridError(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [clientId, pid, deliverableTier, reports.length]);

  // Soft gate: HARD-disable Generate only when candidates exist but NONE is
  // proceed-able (boundary + entrance) — nothing to compose. Unknown/loading →
  // permissive (never a false block). Single-candidate → unchanged behaviour.
  const gridBlocksGenerate = !!grid && grid.total >= 1 && grid.proceedable === 0;
  // ADR-7 — a multi-property COMPARISON must not be DELIVERED to the client until
  // staff confirms the recommended winner (nothing AI-inferred ships unconfirmed).
  const deliverNeedsWinner = !!grid && grid.isComparison && !grid.recommendedValid;

  async function doGenerate() {
    if (busy || !clientId || !deliverableTier) return;
    setBusy('generate');
    setBlockReason(null);
    try {
      const res = await composeReport({ clientId, projectId: pid, tier: deliverableTier, forceRegen: true });
      onMsg(`${deliverableTier} report generated (v${res.version_n}). Review, then Approve & Deliver.`);
    } catch (e) {
      // Surface a friendly message for the most common failure: no floor/intake
      // data saved yet. The server throws INTAKE_MISSING when the client has no
      // intake doc at all — happens on a brand-new project before the staff has
      // saved any floor boundary data in the Technical Intake panel below.
      const msg = (e as Error).message || '';
      const details = (e as { details?: { code?: string } }).details;
      const isIntakeMissing =
        details?.code === 'INTAKE_MISSING' ||
        /no intake/i.test(msg) ||
        /intake.*missing/i.test(msg);
      if (isIntakeMissing) {
        onMsg(
          `Cannot generate yet — no floor data saved. Use the Technical Intake panel below: ` +
          `add at least one floor, draw the boundary, and press "Save all floors" first.`,
        );
      } else {
        onMsg(`Generate failed: ${msg}`);
      }
    } finally {
      setBusy(null);
    }
  }

  async function doApproveAndDeliver() {
    if (busy || !clientId || !deliverableTier || !latest) return;
    setBusy('deliver');
    setBlockReason(null);
    try {
      // 1. Approve (idempotent-ish: if already approved, the CF errors only on
      //    a delivered report — a draft flips cleanly). Skip the approve call
      //    when already approved so a re-try after a gate block goes straight
      //    to deliver.
      if (status !== 'approved') {
        await approveReport({
          clientId,
          projectId: pid,
          tier: deliverableTier,
          runId: latest.run_id,
          versionN: latest.version_n,
        });
      }
      // 2. Deliver — server re-checks money-before-delivery + curation.
      const res = await deliverReport({
        clientId,
        projectId: pid,
        tier: deliverableTier,
        runId: latest.run_id,
        versionN: latest.version_n,
      });
      onMsg(
        `Report delivered to the client conversation · ${deliverableTier} v${latest.version_n} ` +
          `(msg ${res.messageId.slice(0, 6)}…).`,
      );
    } catch (e) {
      // The server's failed-precondition message IS the block reason
      // ("Blocked: stage payment not received (stage1)" etc.). Surface it.
      const reason = (e as Error).message || 'Delivery blocked.';
      setBlockReason(reason);
      onMsg(reason);
    } finally {
      setBusy(null);
    }
  }

  const statusTag =
    status === 'delivered' ? <Tag kind="val">delivered</Tag>
    : status === 'approved' ? <Tag kind="val">approved</Tag>
    : status === 'draft' ? <Tag kind="warn">draft</Tag>
    : <Tag kind="default">not generated</Tag>;

  return (
    <Box
      title="Composer & delivery"
      note="Generate a draft · approve · deliver to the client conversation (money-before-delivery enforced server-side)"
    >
      {!deliverableTier ? (
        <p style={{ fontSize: 13, color: 'var(--ink-mute)', lineHeight: 1.6 }}>
          Report generation is wired for <b>L1A</b> and <b>L2B</b> in this slice.
          This project's tier (<b>{tierCode}</b>) is not yet deliverable through
          the Composer — its template lands in a later slice.
        </p>
      ) : !clientId ? (
        <p style={{ fontSize: 13, color: 'var(--ink-mute)', lineHeight: 1.6 }}>
          No client is linked to this project. Link a client (Edit project →
          re-link) before generating a report.
        </p>
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.6, margin: '0 0 4px' }}>
            The Composer assembles the {deliverableTier} deliverable as an
            internal <b>draft</b>. Generation is not assignee-gated (any staff
            may generate). <b>Approve &amp; Deliver</b> is gated to the project
            assignee or an admin and is re-checked server-side —
            money-before-delivery + curation — before the report is released
            into the client conversation.
          </p>
          {deliverableTier === 'L1A' && (
            <p style={{ fontSize: 12, color: 'var(--ink-mute)', lineHeight: 1.6, margin: '0 0 4px',
              padding: '8px 10px', background: 'var(--inset)', borderRadius: 'var(--r-sm)',
              border: '1px solid var(--rule)' }}>
              <b>Before generating:</b> save at least one floor boundary in the
              Technical Intake panel below. The Composer reads floor/entrance data
              from the intake. Without it the report will be blank.{' '}
              <b>Family members</b> for Sections E &amp; F (Astro-Vastu room
              allocation) come from the <em>client's intake form</em> (the
              form the client fills in) — not from the Participants panel. If
              the client has not submitted the intake, §E/§F will show a
              "pending" note in the draft.
            </p>
          )}

          {/* clean-flow spec §4.2 — a DISTINCT, visible fetch-error state (gating
              stays permissive; this just tells the staff the candidate read failed
              rather than silently showing "no candidates"). */}
          {deliverableTier === 'L1A' && gridError && (
            <p style={{ fontSize: 12, color: 'var(--warn, #9a6a12)', margin: '8px 0 0' }}>
              Couldn’t load candidate grid status (check the connection) — generation
              gating is left open; reopen the project to retry.
            </p>
          )}

          {/* Step 3 (revised ADR-4) — candidate grid-analysis subset banner (L1A multi-property). */}
          {deliverableTier === 'L1A' && grid && grid.total >= 2 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                margin: '10px 0 2px',
                padding: '8px 12px',
                borderRadius: 'var(--r-sm)',
                border: '1px solid var(--rule)',
                background: '#fff',
                fontSize: 12.5,
              }}
            >
              <strong>Candidates</strong>
              <Tag kind="val">{grid.proceedable} ready</Tag>
              {grid.pendingIds.length > 0 && (
                <Tag kind="warn">{grid.pendingIds.length} pending — excluded</Tag>
              )}
              <span style={{ color: 'var(--ink-mute)' }}>
                {grid.proceedable === 0
                  ? '→ trace a boundary + mark an entrance on a property below before generating'
                  : grid.isComparison
                    ? `→ Generate compares the ${grid.proceedable} ready candidates${grid.pendingIds.length ? ' (pending ones are left out of this report)' : ''}`
                    : '→ only 1 ready — Generate produces a single-property report'}
              </span>
              {grid.isComparison && (
                grid.recommendedValid ? (
                  <Tag kind="val" title="A ready candidate is set as the recommended winner — clear to deliver">
                    winner confirmed
                  </Tag>
                ) : (
                  <Tag kind="warn" title="Set the Recommended property (Technical Intake panel) to a ready candidate before delivering">
                    winner not confirmed
                  </Tag>
                )
              )}
            </div>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              margin: '10px 0 2px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontSize: 12.5, color: 'var(--ink-soft)' }}>Report status:</span>
            {statusTag}
            {latest && (
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                {deliverableTier} v{latest.version_n}
                {latest.storage_path ? ' · PDF stored' : ''}
              </span>
            )}
            {/* §1.3 — view/download the stored DRAFT. The delivered-only CF
                (getReportDownloadUrl) 404s on a draft; instead we open the
                artefact's own stored downloadURL (the working PDF). */}
            {(latest?.artefact_file_id || latest?.storage_path) && (
              <Btn3
                intent="ghost"
                size="sm"
                onClick={async () => {
                  try {
                    const url = await getDraftArtefactUrl(pid, latest?.artefact_file_id);
                    window.open(url, '_blank');
                  } catch (e) {
                    onMsg(`Couldn't open the draft: ${(e as Error).message}`);
                  }
                }}
              >
                View / Download draft
              </Btn3>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
            <Btn3
              intent="au"
              disabled={!mayGenerate || busy != null || gridBlocksGenerate}
              onClick={doGenerate}
              title={
                !mayGenerate
                  ? 'A staff or admin session is required to generate a report'
                  : gridBlocksGenerate
                    ? 'No candidate is ready yet — trace a boundary + mark an entrance on a property below first (AUOs can follow)'
                    : latest
                      ? 'Regenerate the report draft (replaces the current draft). Approve & Deliver finalises it.'
                      : 'Generate the report draft. Approve & Deliver finalises it.'
              }
            >
              {busy === 'generate'
                ? latest
                  ? 'Regenerating…'
                  : 'Generating…'
                : latest
                  ? '⟡ Regenerate Draft'
                  : '⟡ Generate Draft'}
            </Btn3>

            <Btn3
              intent="ink"
              disabled={!mayCommit || busy != null || !latest || status === 'delivered' || deliverNeedsWinner}
              onClick={doApproveAndDeliver}
              title={
                !mayCommit
                  ? 'Only the project assignee or an admin can approve & deliver'
                  : !latest
                    ? 'Generate a draft first'
                    : status === 'delivered'
                      ? 'Already delivered to the client'
                      : deliverNeedsWinner
                        ? 'Confirm the Recommended property (a ready candidate) in the Technical Intake panel before delivering the comparison'
                        : 'Approve and release this report into the client conversation'
              }
            >
              {busy === 'deliver'
                ? 'Delivering…'
                : status === 'delivered'
                  ? '✓ Delivered'
                  : 'Approve & Deliver'}
            </Btn3>

            {!mayCommit && (
              <span style={{ fontSize: 12, color: 'var(--ink-mute)', alignSelf: 'center' }}>
                Curation gate — assignee/admin only
              </span>
            )}
          </div>

          {blockReason && (
            <div
              style={{
                marginTop: 12,
                padding: '10px 12px',
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r-sm)',
                background: 'var(--inset)',
                fontSize: 12.5,
                color: 'var(--ink-soft)',
                lineHeight: 1.5,
              }}
            >
              <b style={{ color: 'var(--ink)' }}>Delivery blocked.</b> {blockReason}
              {' '}Clear the outstanding payment gate (Payment gates below, or an
              admin override) and the curation gate, then retry.
            </div>
          )}
        </>
      )}
    </Box>
  );
}

/* ════════════════════ Module 4 — Property Linking Panel (AD-013) ════════════════════
 *
 * Lets staff link existing Property entities (P#####) to this project as
 * "candidate" or "primary" references. Bidirectional writes: the project's
 * propertyRefs[] and the property's engagementRefs[]. Also provides a quick
 * link to create a brand-new property pre-linked to this project via the
 * ?linkTo= query param.
 */
function PropertyLinkingPanel({
  projectId,
  propertyRefs,
  allProperties,
  onMsg,
  clientId,
  intakeTier,
  tierCode,
  onLinkChange,
}: {
  projectId: string;
  propertyRefs: PropertyRef[];
  allProperties: LiveProperty[];
  onMsg: (msg: string) => void;
  clientId?: string | null;
  intakeTier?: string;
  tierCode?: string;
  /** Bumped after EVERY candidate/property link-shape write (link, unlink,
   *  candidate-stamp, add-candidate, register+link) — the host page's shared
   *  `linkRefreshNonce`, so every link-derived read (grid summary, stage dots,
   *  Astro-Vastu resolve) re-resolves in-session (owner ruling 2026-07-03:
   *  a correction must propagate). */
  onLinkChange?: () => void;
}) {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pickedPropertyId, setPickedPropertyId] = useState('');
  const [pickedRole, setPickedRole] = useState<'primary' | 'candidate'>('candidate');
  const [busy, setBusy] = useState(false);

  // ── AD-013 / L1A — candidate↔property join surfacing (clean-flow §3.2 carry-over).
  // Load the candidate slots so Step 1 can show: the "auto-linked P#####" note for
  // the candidates Prompt-1's pure join stamped unambiguously, AND a manual
  // "link candidate → P#####" picker for the residual `ambiguous[]` cases. L1A only.
  const isL1AStep1 = (tierCode || '').split(/\s*[·•]/)[0].trim().toUpperCase() === 'L1A';
  const [candidates, setCandidates] = useState<PropertyInput[] | null>(null);
  const [recommendedId, setRecommendedId] = useState('');
  const [joinNonce, setJoinNonce] = useState(0);
  const [pickBusy, setPickBusy] = useState(false);
  useEffect(() => {
    if (!isL1AStep1 || !clientId || !intakeTier) {
      setCandidates(null);
      return;
    }
    let alive = true;
    loadPropertiesIntake({ clientId, projectId }, intakeTier)
      .then(async (r) => {
        if (!alive) return;
        setRecommendedId(r.recommendedPropertyId || '');
        // Connector backstop (Bug 1): stamp candidate↔registry FK on Step-1 load
        // too, not only at link-time or inside the marking surface. PURE join,
        // persisted only when unambiguous; idempotent so the effect can't loop.
        const { joined, changed } = joinCandidatesToLinkedProperties(
          r.form.properties,
          propertyRefs.map((ref) => ({ propertyId: ref.propertyId })),
        );
        if (changed) {
          try {
            await saveProperties(
              { clientId, projectId },
              { north_rotation: r.form.north_rotation, properties: joined },
              { uid: null, via: 'staff' },
              { recommendedPropertyId: r.recommendedPropertyId || '' },
              intakeTier,
            );
          } catch {
            /* non-fatal — the StaffTechnicalPanel load-effect join is the backstop */
          }
        }
        if (alive) setCandidates(changed ? joined : r.form.properties);
      })
      .catch(() => alive && setCandidates([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isL1AStep1, clientId, intakeTier, propertyRefs.length, joinNonce]);

  // Manually stamp a candidate slot with a linked registry property (the residual
  // ambiguous case the auto-join intentionally left). Disjoint persist via the
  // same saveProperties path; reload so the note/picker refresh.
  async function pickCandidateProperty(candidateId: string, propertyId: string) {
    if (!clientId || !intakeTier || !candidates) return;
    setPickBusy(true);
    try {
      // stampCandidateProperty resets the property-specific marks when the slot
      // moves to a DIFFERENT property (owner bug 2026-07-05 — no carry-over).
      const next = candidates.map((c) =>
        c.id === candidateId ? stampCandidateProperty(c, propertyId) : c,
      );
      await saveProperties(
        { clientId, projectId },
        { north_rotation: '', properties: next },
        { uid: null, via: 'staff' },
        { recommendedPropertyId: recommendedId }, // preserve the §D pick (else clobbered to '')
        intakeTier,
      );
      setCandidates(next);
      setJoinNonce((n) => n + 1);
      onLinkChange?.();
      onMsg(`Linked candidate → ${propertyId}.`);
    } catch (e) {
      onMsg(`Link failed: ${(e as Error).message}`);
    } finally {
      setPickBusy(false);
    }
  }

  // Bug 3 — a linked property (projects.propertyRefs[]) with NO candidate to grade
  // is otherwise invisible (grid/rollup iterate candidates, never refs). EXPLICIT
  // user action: mint a new candidate slot pre-stamped with that propertyId and
  // persist it via the same saveProperties path. After it the candidate appears in
  // the grid rollup and is gradeable. NOT auto-created — preserves the two-action
  // link/grade model (AD-048).
  async function addCandidateForRef(propertyId: string) {
    if (!clientId || !intakeTier) return;
    setPickBusy(true);
    try {
      // The property is already linked (it's a freeRef). Route through the ONE
      // unified path: linkPropertyAsCandidate re-links idempotently (no dup ref)
      // and ensures a pre-stamped candidate slot exists (absorbing an unjoined
      // candidate first, else appending). The load effect (joinNonce) refreshes.
      const role = propertyRefs.find((r) => r.propertyId === propertyId)?.role ?? 'candidate';
      await linkPropertyAsCandidate(projectId, clientId, propertyId, role, intakeTier, {
        ensureCandidate: true,
        refs: propertyRefs.map((r) => ({ propertyId: r.propertyId })),
      });
      setJoinNonce((n) => n + 1);
      onLinkChange?.();
      onMsg(`Added candidate for ${propertyId} — open the marking surface to grade it.`);
    } catch (e) {
      onMsg(`Add candidate failed: ${(e as Error).message}`);
    } finally {
      setPickBusy(false);
    }
  }

  // ── Register-this-property banner (Item 7) ──
  // Show when: no properties linked AND intake has a property_address.
  const [intakeAddress, setIntakeAddress] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [registerBusy, setRegisterBusy] = useState(false);

  useEffect(() => {
    if (propertyRefs.length > 0 || !clientId || !intakeTier) return;
    loadIntake({ clientId, projectId }, intakeTier).then(({ form }) => {
      if (form.property_address?.trim()) {
        setIntakeAddress(form.property_address.trim());
      }
    }).catch(() => { /* non-fatal */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, intakeTier, propertyRefs.length]);

  async function handleRegisterFromIntake() {
    if (!clientId || !intakeTier || !intakeAddress) return;
    setRegisterBusy(true);
    try {
      // Create the property from the intake data.
      const pid = await createProperty(
        { address: intakeAddress },
        'system',
      );
      // ONE unified link path (Gap #2): linkPropertyAsCandidate writes BOTH refs
      // (project.propertyRefs[] + property.engagementRefs[], idempotent — no dup)
      // AND, for L1A, ensures a pre-stamped candidate slot exists. Supersedes the
      // old linkPropertyToProject + stampJoinAfterLink pair.
      const role: 'primary' | 'candidate' = isL1AStep1 ? 'candidate' : 'primary';
      await linkPropertyAsCandidate(projectId, clientId, pid, role, intakeTier, {
        ensureCandidate: isL1AStep1,
        refs: propertyRefs.map((r) => ({ propertyId: r.propertyId })),
      });
      // Write propertyId back to the intake doc (project-scoped, AD-030).
      await savePropertyLink({ clientId, projectId }, intakeTier, pid, null, 'system');
      onLinkChange?.();
      onMsg(`Property ${pid} registered and linked.`);
      setBannerDismissed(true);
    } catch (e) {
      onMsg(`Register failed: ${String(e)}`);
    } finally {
      setRegisterBusy(false);
    }
  }

  // Filter properties by search text (P##### code or address substring)
  const filtered = allProperties.filter((prop) => {
    if (!searchText.trim()) return true;
    const q = searchText.toLowerCase();
    return (
      prop.propertyId.toLowerCase().includes(q) ||
      (prop.address ?? '').toLowerCase().includes(q)
    );
  });

  async function doLink() {
    if (!pickedPropertyId) return;
    setBusy(true);
    try {
      // ONE unified link path (Gap #2): writes both refs + (L1A) ensures a
      // pre-stamped candidate. ensureCandidate is gated on L1A + intake context.
      await linkPropertyAsCandidate(
        projectId,
        clientId ?? '',
        pickedPropertyId,
        pickedRole,
        intakeTier ?? '',
        {
          ensureCandidate: isL1AStep1 && !!clientId && !!intakeTier,
          refs: propertyRefs.map((r) => ({ propertyId: r.propertyId })),
        },
      );
      setJoinNonce((n) => n + 1);
      onLinkChange?.();
      onMsg(`Linked ${pickedPropertyId} as ${pickedRole}`);
      setDrawerOpen(false);
      setPickedPropertyId('');
      setSearchText('');
    } catch (e) {
      onMsg(`Link failed: ${String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  async function doUnlink(propertyId: string) {
    setBusy(true);
    try {
      await unlinkPropertyFromProject(projectId, propertyId);
      // TWO-LANES fix (owner bug 2026-07-03): the unlink above only cleans
      // Lane B (projects.propertyRefs[] + engagementRefs[]). Clear the Lane-A
      // candidate STAMP (answers.properties[i].propertyId) too, or Intake keeps
      // showing "✓ auto-linked P#####" for a link that no longer exists.
      // Best-effort: a stamp-clear failure must not undo the unlink.
      if (clientId && intakeTier) {
        try {
          await clearCandidatePropertyStamp(
            { clientId, projectId },
            intakeTier,
            propertyId,
          );
        } catch (e) {
          console.warn(
            `[doUnlink] candidate-stamp clear for ${propertyId} failed (Intake note may be stale):`,
            (e as Error).message,
          );
        }
      }
      // reload the candidate box so the "auto-linked" note drops immediately.
      setJoinNonce((n) => n + 1);
      // The unlink IS the owner's correction path — the bump makes every
      // link-derived surface (Evaluate list, grid summary, stage dots,
      // Astro-Vastu marked bedrooms) drop it in-session, no reload.
      onLinkChange?.();
      onMsg(`Unlinked ${propertyId}`);
    } catch (e) {
      onMsg(`Unlink failed: ${String(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box title="Linked Properties">
      {/* AD-013 / L1A — candidate↔property join status + manual picker (clean-flow
          §3.2). Auto-joined candidates show a subtle "auto-linked P#####" note;
          the residual ambiguous unjoined ones get a "link candidate → P#####"
          picker (the cases the pure join intentionally left for staff). */}
      {isL1AStep1 && candidates && (() => {
        const freeRefs = propertyRefs.filter(
          (r) => !candidates.some((c) => c.propertyId === r.propertyId),
        );
        const unjoined = candidates.filter((c) => !c.propertyId);
        // Bug 3: render even with ZERO candidates when there's a free linked ref to
        // surface (the outer guard no longer requires candidates.length > 0). Only
        // bail when there is genuinely nothing to show.
        if (candidates.every((c) => !c.propertyId) && freeRefs.length === 0) return null;
        return (
          <div
            style={{
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-sm)',
              padding: '10px 12px',
              marginBottom: 12,
              background: 'var(--inset, #f6f7f9)',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 8 }}>
              Candidate → registry property
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {candidates.map((c, i) => {
                const name = c.label?.trim() || `Property ${String.fromCharCode(65 + i)}`;
                return (
                  <div
                    key={c.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}
                  >
                    <strong style={{ color: 'var(--ink)' }}>{name}</strong>
                    {c.propertyId ? (
                      (() => {
                        // READ-SIDE TRUTHFULNESS (owner bug 2026-07-03): a stamp
                        // pointing at a property that is trashed / archived /
                        // missing from the registry must not render a healthy
                        // "✓ auto-linked" — the write-side cleaning (unlink /
                        // trash cascade) handles new corrections; this catches
                        // pre-fix data. Only judged once the registry stream
                        // has loaded (allProperties non-empty), so there is no
                        // false "broken" flash on first paint.
                        const reg = allProperties.find(
                          (p) => p.propertyId === c.propertyId,
                        );
                        const broken =
                          allProperties.length > 0 &&
                          (!reg || reg.trashed === true || reg.archived === true);
                        return broken ? (
                          <span style={{ color: 'var(--ink-mute)' }}>
                            link broken — {c.propertyId} was removed from the
                            registry
                          </span>
                        ) : (
                          <span style={{ color: 'var(--good)', fontWeight: 600 }}>
                            ✓ auto-linked {c.propertyId}
                          </span>
                        );
                      })()
                    ) : freeRefs.length > 0 ? (
                      <select
                        aria-label={`Link ${name} to a registry property`}
                        disabled={pickBusy}
                        defaultValue=""
                        onChange={(e) => e.target.value && pickCandidateProperty(c.id, e.target.value)}
                        style={{
                          border: '1px solid var(--rule)',
                          borderRadius: 'var(--r-sm)',
                          padding: '5px 8px',
                          fontSize: 12.5,
                          fontFamily: 'inherit',
                          background: '#fff',
                        }}
                      >
                        <option value="">— link to P##### —</option>
                        {freeRefs.map((r) => (
                          <option key={r.propertyId} value={r.propertyId}>
                            {r.propertyId}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ color: 'var(--warn)' }}>
                        link a registry property below to bind this candidate
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Bug 3 — each linked property with NO candidate yet gets an explicit
                "+ Add candidate for P#####". Linking a property and adding a
                candidate to grade are two deliberate actions; this surfaces the
                second so a linked-but-orphaned property is gradeable in one click. */}
            {freeRefs.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  marginTop: candidates.length > 0 ? 10 : 0,
                  paddingTop: candidates.length > 0 ? 10 : 0,
                  borderTop: candidates.length > 0 ? '1px dashed var(--rule)' : undefined,
                }}
              >
                {freeRefs.map((r) => (
                  <div
                    key={`free-${r.propertyId}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}
                  >
                    <span style={{ color: 'var(--warn)' }}>
                      {r.propertyId} is linked but has no candidate yet
                    </span>
                    <Btn3
                      intent="ink"
                      size="sm"
                      disabled={pickBusy}
                      onClick={() => addCandidateForRef(r.propertyId)}
                    >
                      + Add candidate for {r.propertyId}
                    </Btn3>
                  </div>
                ))}
              </div>
            )}
            {unjoined.length > 0 && freeRefs.length > 0 && (
              <p style={{ fontSize: 11, color: 'var(--ink-mute)', margin: '8px 0 0', lineHeight: 1.5 }}>
                A single candidate ↔ single property links itself automatically; pick here only when
                the mapping is ambiguous.
              </p>
            )}
          </div>
        );
      })()}
      {/* Register-from-intake amber banner — shows when no property linked but intake has an address */}
      {propertyRefs.length === 0 && !bannerDismissed && intakeAddress && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fbbf24',
            borderRadius: 'var(--r-sm)',
            padding: '10px 12px',
            marginBottom: 12,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#92400e', marginBottom: 4 }}>
              Register this property
            </div>
            <div style={{ fontSize: 12, color: '#78350f' }}>
              This project has a property address in the intake ({intakeAddress}) but no
              permanent Property entity. Creating one lets you reuse the floor plan, boundary,
              and grid across future projects.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center', marginTop: 2 }}>
            <Btn3
              intent="au"
              size="sm"
              onClick={handleRegisterFromIntake}
              disabled={registerBusy}
            >
              Register →
            </Btn3>
            <Btn3
              intent="ink"
              size="sm"
              onClick={() => setBannerDismissed(true)}
            >
              ✕
            </Btn3>
          </div>
        </div>
      )}
      {/* Existing links */}
      {propertyRefs.length === 0 && !intakeAddress && (
        <EmptyNote title="No properties linked yet">Click "+ Link property" below to attach a property to this project.</EmptyNote>
      )}
      {propertyRefs.map((ref) => {
        const prop = allProperties.find((p) => p.propertyId === ref.propertyId);
        return (
          <div
            key={ref.propertyId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '8px 0',
              borderBottom: '1px solid var(--rule)',
            }}
          >
            <Tag kind={ref.role === 'primary' ? 'find' : 'default'}>
              {ref.role === 'primary' ? 'Primary' : 'Candidate'}
            </Tag>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{ref.propertyId}</span>
            {prop?.address && (
              <span style={{ fontSize: 12, color: 'var(--ink-2)', flex: 1 }}>{prop.address}</span>
            )}
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => navigate(`/properties/${ref.propertyId}?openGrid=1&from=${projectId}`)}
            >
              Open →
            </Btn3>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => void doUnlink(ref.propertyId)}
              disabled={busy}
            >
              Unlink
            </Btn3>
          </div>
        );
      })}

      {/* Action bar */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Btn3 intent="ghost" size="sm" onClick={() => setDrawerOpen((v) => !v)}>
          {drawerOpen ? 'Cancel' : '+ Link property'}
        </Btn3>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => navigate(`/properties?linkTo=${projectId}`)}
        >
          + New property
        </Btn3>
      </div>

      {/* Search drawer */}
      {drawerOpen && (
        <div
          style={{
            marginTop: 12,
            padding: 12,
            border: '1px solid var(--rule)',
            borderRadius: 'var(--r-sm)',
            background: 'var(--surface)',
          }}
        >
          <input
            type="text"
            placeholder="Search by P##### or address…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              width: '100%',
              fontSize: 13,
              padding: '6px 8px',
              border: '1px solid var(--rule)',
              borderRadius: 'var(--r-sm)',
              marginBottom: 8,
              background: 'var(--surface)',
              color: 'var(--ink)',
            }}
          />
          {/* Results list */}
          <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
            {filtered.length === 0 && (
              <EmptyNote title="No properties match" />
            )}
            {filtered.map((prop) => {
              const alreadyLinked = propertyRefs.some((r) => r.propertyId === prop.propertyId);
              return (
                <div
                  key={prop.propertyId}
                  onClick={() => !alreadyLinked && setPickedPropertyId(prop.propertyId)}
                  style={{
                    padding: '6px 8px',
                    cursor: alreadyLinked ? 'default' : 'pointer',
                    background: pickedPropertyId === prop.propertyId ? 'var(--accent-subtle)' : 'transparent',
                    borderRadius: 'var(--r-sm)',
                    opacity: alreadyLinked ? 0.5 : 1,
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{prop.propertyId}</span>
                  {prop.address && (
                    <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{prop.address}</span>
                  )}
                  {alreadyLinked && <Tag kind="default">linked</Tag>}
                </div>
              );
            })}
          </div>

          {/* Role picker + confirm */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <select
              value={pickedRole}
              onChange={(e) => setPickedRole(e.target.value as 'primary' | 'candidate')}
              style={{
                fontSize: 13,
                padding: '6px 8px',
                border: '1px solid var(--rule)',
                borderRadius: 'var(--r-sm)',
                background: 'var(--surface)',
                color: 'var(--ink)',
              }}
            >
              <option value="candidate">Candidate</option>
              <option value="primary">Primary</option>
            </select>
            <Btn3
              intent="grn"
              size="sm"
              onClick={() => void doLink()}
              disabled={busy || !pickedPropertyId}
            >
              {busy ? 'Linking…' : 'Link'}
            </Btn3>
          </div>
        </div>
      )}
    </Box>
  );
}

/* ════════════════════ Module 4b — Linked Group Panel (Astro-Vastu) ════════════
 *
 * OWNER CORRECTION 2026-07-02 — a project's astro subject is an AVBP **Group**
 * (a `families`-collection record), stored as `project.linked_group_id` (the
 * AVBP Group doc id). This box links that reference DIRECTLY — it no longer
 * derives an `astroFamilyId` and no longer calls the astro-store callable
 * `linkProjectAstroFamily`. Forge's Astro-Vastu resolve reads
 * `linked_group_id`, loads the Group's members from AVBP, and computes each
 * member's chart from their birth details.
 *
 * PICK → LINK: `setProjectLinkedGroup(projectId, group.id)` writes the AVBP
 * Group id (and clears any linked Contacts — either/or). DISPLAY: the linked
 * Group is recovered by matching `group.id === project.linked_group_id` against
 * the live `families` stream (name + member count).
 *
 * Any AVBP Group is linkable as long as it exists — there is no "not
 * astro-ready" failure path. Charts resolve per-member once birth details are
 * present (Forge surfaces that per-member in the Astro-Vastu tab); a Group with
 * no astro-ready members still links cleanly here.
 *
 * Optimistic: link/unlink flips local state immediately; a failure reverts and
 * toasts. The picked Group's name/count is carried optimistically until the
 * `families` stream resolves the id.
 */
function LinkedGroupPanel({
  projectId,
  groupId,
  onMsg,
  onLinkChange,
}: {
  projectId: string;
  /** = project.linked_group_id — the AVBP Group doc id (`families/{id}`), or null. */
  groupId: string | null;
  onMsg: (msg: string) => void;
  onLinkChange: () => void;
}) {
  // Live `families` directory — powers both the picker and the linked-Group
  // display resolution (linked_group_id → Group).
  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(false);

  // Optimistic mirror of the AUTHORITATIVE link (project.linked_group_id).
  const [linkedId, setLinkedId] = useState<string | null>(groupId);
  // Name/count carried from the pick so the linked display is correct before the
  // `families` stream resolves the id.
  const [optimistic, setOptimistic] = useState<GroupOption | null>(null);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLinkedId(groupId);
  }, [groupId]);

  useEffect(() => {
    const unsub = listenFamilies(
      (g) => {
        setGroups(g);
        setGroupsLoading(false);
      },
      {
        onErr: () => {
          setGroupsError(true);
          setGroupsLoading(false);
        },
      },
    );
    return unsub;
  }, []);

  const groupOptions: GroupOption[] = useMemo(() => groups.map(groupToOption), [groups]);

  // Resolve the linked Group from the AVBP `families` stream by doc id. Falls
  // back to the optimistic pick (name/count) until the stream catches up.
  const linkedGroup = useMemo<GroupOption | null>(() => {
    if (!linkedId) return null;
    const g = groups.find((x) => x.id === linkedId);
    if (g) return groupToOption(g);
    if (optimistic && optimistic.id === linkedId) return optimistic;
    return null;
  }, [groups, linkedId, optimistic]);

  async function doLinkGroup(group: GroupOption) {
    setPicking(false);
    const prevId = linkedId;
    const prevOpt = optimistic;
    setBusy(true);
    // Optimistic: show the picked group immediately.
    setLinkedId(group.id);
    setOptimistic(group);
    try {
      await setProjectLinkedGroup(projectId, group.id);
      onLinkChange();
      onMsg(`Linked group: ${group.name}. The Astro-Vastu tab now resolves.`);
    } catch (e) {
      setLinkedId(prevId); // revert
      setOptimistic(prevOpt);
      // Never surface the raw Firebase code (e.g. "internal"/"permission-denied").
      console.error('setProjectLinkedGroup failed', e);
      onMsg('Couldn’t link the group — please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function doUnlink() {
    const prevId = linkedId;
    const prevOpt = optimistic;
    setBusy(true);
    setLinkedId(null); // optimistic
    setOptimistic(null);
    try {
      await setProjectLinkedGroup(projectId, null);
      onLinkChange();
      onMsg('Group unlinked. The Astro-Vastu tab has no charts to resolve until you link a group or contacts.');
    } catch (e) {
      setLinkedId(prevId); // revert
      setOptimistic(prevOpt);
      console.error('setProjectLinkedGroup (unlink) failed', e);
      onMsg('Couldn’t unlink — please try again.');
    } finally {
      setBusy(false);
    }
  }

  const showLinked = linkedId != null;
  // Name/count to show for the linked state: the resolved Group when found, else
  // the raw id (until the stream resolves it).
  const linkedName = linkedGroup?.name ?? linkedId ?? '';
  const linkedCount = linkedGroup?.memberCount ?? null;

  return (
    <Box title="Linked Group" allowOverflow>
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px', lineHeight: 1.5 }}>
        The linked group drives the Astro-Vastu tab — it’s checked against each member’s birth
        chart. A project is for a group (a family or organization) — link one from Manage → Groups.
      </p>

      {showLinked ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{linkedName}</span>
          {linkedCount != null && (
            <Tag kind="tag">
              {linkedCount} member{linkedCount === 1 ? '' : 's'}
            </Tag>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Btn3 intent="ghost" size="sm" disabled={busy} onClick={() => setPicking((v) => !v)}>
              {picking ? 'Cancel' : 'Change'}
            </Btn3>
            <Btn3 intent="ghost" size="sm" disabled={busy} onClick={() => void doUnlink()}>
              {busy ? 'Working…' : 'Unlink'}
            </Btn3>
          </div>
        </div>
      ) : (
        !picking && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <EmptyNote title="No group linked yet">
              Link a group so it can drive the Astro-Vastu tab.
            </EmptyNote>
            <Btn3 intent="grn" size="sm" disabled={busy} onClick={() => setPicking(true)}>
              Link a group
            </Btn3>
          </div>
        )
      )}

      {picking && (
        <div style={{ marginTop: 10 }}>
          <GroupPicker
            autoFocus
            groups={groupOptions}
            loading={groupsLoading}
            error={groupsError}
            onPick={(group) => void doLinkGroup(group)}
            ariaLabel={showLinked ? 'Change the linked group' : 'Search and link a group'}
          />
          {showLinked && (
            <div style={{ marginTop: 8 }}>
              <Btn3 intent="ghost" size="sm" disabled={busy} onClick={() => setPicking(false)}>
                Cancel
              </Btn3>
            </div>
          )}
        </div>
      )}
    </Box>
  );
}

/* ════════════════════ Piece 1 · step ⑤ — Linked Contacts Panel (Astro-Vastu) ═══
 *
 * OWNER CORRECTION 2026-07-02 — the EITHER/OR partner to LinkedGroupPanel. A
 * project's astro subject can be an AVBP **Group** OR 1–2 individual AVBP
 * **Contacts** (this box), never both. This box stores the AVBP Contact ids
 * (`clients/{id}` doc ids) on `project.linked_contact_ids` — it no longer
 * derives a `${clientNo}_primary` astro-person id and no longer calls the
 * astro-store callable `linkProjectAstroPersons`.
 *
 * PICK → LINK: staff pick up to 2 Contacts with the SHARED `ClientPicker`; the
 * chosen `clients/{id}` doc ids are written with `setProjectLinkedContacts`,
 * which CLEARS the group link (either/or, enforced in the store). We bump the
 * shared `linkRefreshNonce` via `onLinkChange` so the Astro-Vastu tab
 * re-resolves. Forge resolves each Contact's chart from its birth details.
 *
 * Any AVBP Contact is linkable as long as it exists — there is no "no astro
 * chart record" failure path. A linked Contact that is missing birth details on
 * its AVBP master shows a calm per-member "needs birth details" hint (charts
 * resolve once they're present).
 *
 * Optimistic: link/unlink flips local state immediately; a failure reverts and
 * toasts. Display names resolve from the AVBP `clients` masters.
 */

/** True when a contact master carries the birth details astro charts need. */
function contactHasBirthDetails(c: LiveClient | undefined): boolean {
  return !!(c && c.dob && c.tob && c.pob);
}

/** One linked contact in the panel's optimistic state. */
interface LinkedContact {
  /** The AVBP Contact id — the `clients/{id}` doc id (= C##### for front-door). */
  clientNo: string;
  /** Display name (resolved from the AVBP client master, or the picker). */
  name: string;
}

const MAX_ASTRO_CONTACTS = 2;

function LinkedContactsPanel({
  projectId,
  contactIds,
  groupLinked,
  onMsg,
  onLinkChange,
}: {
  projectId: string;
  /** = project.linked_contact_ids — AVBP Contact ids (`clients/{id}`), or null. */
  contactIds: string[] | null;
  /** True when the project currently has a GROUP link (the either/or sibling). */
  groupLinked: boolean;
  onMsg: (msg: string) => void;
  onLinkChange: () => void;
}) {
  // Client masters — powers display-name + the birth-details readiness check.
  const [clients, setClients] = useState<LiveClient[]>([]);

  useEffect(() => {
    const unsub = listenClients(setClients, {});
    return unsub;
  }, []);

  // Resolve an AVBP Contact id → its master. Keyed by BOTH the doc id (cid) and
  // the clientNo so a stored id resolves whether it is the doc id or the C#####.
  const clientById = useMemo(() => {
    const m = new Map<string, LiveClient>();
    for (const c of clients) {
      m.set(c.cid, c);
      if (c.clientNo) m.set(c.clientNo, c);
    }
    return m;
  }, [clients]);

  // Zip the authoritative id list into a display shape, resolving names from the
  // AVBP client masters (fall back to the id until the stream resolves).
  const fromProps = useMemo<LinkedContact[]>(() => {
    const ids = contactIds ?? [];
    return ids.map((clientNo) => ({
      clientNo,
      name: clientById.get(clientNo)?.name || clientNo,
    }));
  }, [contactIds, clientById]);

  // Optimistic mirror of the AUTHORITATIVE contacts link. Re-synced when the
  // streamed project doc (or the resolved names) changes.
  const [linked, setLinked] = useState<LinkedContact[]>(fromProps);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLinked(fromProps);
  }, [fromProps]);

  // Write the full contacts set (or null to clear). Optimistic + revert-on-fail.
  async function writeContacts(next: LinkedContact[], successMsg: string) {
    const prev = linked;
    const ids = next.map((c) => c.clientNo);
    setBusy(true);
    setLinked(next); // optimistic
    if (next.length > 0) setPicking(false);
    try {
      await setProjectLinkedContacts(projectId, ids.length > 0 ? ids : null);
      onLinkChange();
      onMsg(successMsg);
    } catch (e) {
      setLinked(prev); // revert the optimistic change
      console.error('setProjectLinkedContacts failed', e);
      onMsg('Couldn’t update the linked contacts — please try again.');
    } finally {
      setBusy(false);
    }
  }

  function addContact(row: ClientRow) {
    if (linked.length >= MAX_ASTRO_CONTACTS) return;
    // The AVBP Contact id is the `clients/{id}` doc id (row.clientId).
    const contactId = row.clientId || row.clientNo;
    // Ignore a duplicate pick (same contact already linked).
    if (linked.some((c) => c.clientNo === contactId)) {
      setPicking(false);
      return;
    }
    const next: LinkedContact[] = [
      ...linked,
      { clientNo: contactId, name: row.name || contactId },
    ];
    const noun = groupLinked
      ? ` The group link is cleared — a project’s astro subject is a group OR contacts.`
      : '';
    void writeContacts(next, `Linked contact: ${row.name || contactId}.${noun}`);
  }

  function openPicker() {
    setPicking(true);
  }

  function removeContact(contactId: string) {
    const next = linked.filter((c) => c.clientNo !== contactId);
    void writeContacts(
      next,
      next.length === 0
        ? 'Contacts unlinked. The Astro-Vastu tab has no charts to resolve until you link a group or contacts.'
        : 'Contact removed.',
    );
  }

  function unlinkAll() {
    void writeContacts(
      [],
      'Contacts unlinked. The Astro-Vastu tab has no charts to resolve until you link a group or contacts.',
    );
  }

  const hasLinked = linked.length > 0;
  const canAddMore = linked.length < MAX_ASTRO_CONTACTS;

  return (
    <Box title="Linked Contacts" allowOverflow>
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px', lineHeight: 1.5 }}>
        Link one or two individual contacts as the astro subject when there’s no full
        group. A project’s astro subject is a <b>group OR individual contacts</b> —
        linking contacts here clears the linked group, and vice-versa.
      </p>

      {hasLinked ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {linked.map((c) => {
            const ready = contactHasBirthDetails(clientById.get(c.clientNo));
            return (
              <div key={c.clientNo} style={{ padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>{c.name}</span>
                  <Tag kind="tag">{c.clientNo}</Tag>
                  <AstroLinkBadge linked={ready} />
                  <div style={{ marginLeft: 'auto' }}>
                    <Btn3
                      intent="ghost"
                      size="sm"
                      disabled={busy}
                      onClick={() => removeContact(c.clientNo)}
                    >
                      {busy ? 'Working…' : 'Remove'}
                    </Btn3>
                  </div>
                </div>
                {!ready && (
                  <p
                    style={{
                      margin: '6px 0 0',
                      padding: '8px 10px',
                      fontSize: 11.5,
                      lineHeight: 1.5,
                      color: 'var(--warn-ink)',
                      background: 'var(--warn-wash)',
                      border: '1px solid var(--warn-edge)',
                      borderRadius: 'var(--r-sm)',
                    }}
                  >
                    Needs birth details before charts resolve — add date, time and place
                    of birth on this contact, then the Astro-Vastu tab will resolve.
                  </p>
                )}
              </div>
            );
          })}

          <div style={{ display: 'flex', gap: 8, marginTop: 2, flexWrap: 'wrap' }}>
            {canAddMore && !picking && (
              <Btn3 intent="grn" size="sm" disabled={busy} onClick={openPicker}>
                Add another contact
              </Btn3>
            )}
            <Btn3 intent="ghost" size="sm" disabled={busy} onClick={unlinkAll}>
              {busy ? 'Working…' : 'Unlink all'}
            </Btn3>
          </div>
        </div>
      ) : (
        !picking && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <EmptyNote title="No contacts linked yet">
              Link one or two contacts so their birth charts can drive the Astro-Vastu tab.
            </EmptyNote>
            <Btn3 intent="grn" size="sm" disabled={busy} onClick={openPicker}>
              Link a contact
            </Btn3>
          </div>
        )
      )}

      {picking && canAddMore && (
        <div style={{ marginTop: 10 }}>
          <ClientPicker
            value=""
            onPick={addContact}
            ariaLabel={hasLinked ? 'Search and add another contact' : 'Search and link a contact'}
            placeholder="Type a contact name or number…"
          />
          <div style={{ marginTop: 8 }}>
            <Btn3 intent="ghost" size="sm" disabled={busy} onClick={() => setPicking(false)}>
              Cancel
            </Btn3>
          </div>
        </div>
      )}
    </Box>
  );
}

/* ════════════════════ Module 2 — Participants Panel (AD-009) ════════════════════
 *
 * Shows/edits the per-project participant matrix. On first render, seeds from
 * the legacy clientId/partnerId shortcut fields if participants[] is absent.
 * Admin-only: remove and add participants. Staff: view-only.
 */
function ParticipantsPanel({
  project,
  isAdmin,
  actorUid,
  onMsg,
}: {
  project: LiveProject;
  isAdmin: boolean;
  actorUid: string | null;
  onMsg: (msg: string) => void;
}) {
  const [clients, setClients] = useState<LiveClient[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [pickContactId, setPickContactId] = useState('');
  const [pickRole, setPickRole] = useState<ParticipantRole>('co_client');
  const [pickRelationship, setPickRelationship] = useState('');
  const [busy, setBusy] = useState(false);

  // Stream client masters for the picker
  useEffect(() => {
    const unsub = listenClients(setClients, {});
    return unsub;
  }, []);

  // Seed participants[] from legacy fields on first render if absent
  useEffect(() => {
    if (seeded) return;
    if (!project.participants || project.participants.length === 0) {
      const toSeed = seedParticipantsFromLegacy(project);
      if (toSeed.length > 0 && project.pid) {
        setSeeded(true);
        updateDoc(doc(db, 'projects', project.pid), {
          participants: toSeed,
        }).catch(() => {/* non-fatal — will retry next render */});
      }
    } else {
      setSeeded(true);
    }
  }, [project, seeded]);

  const participants = project.participants ?? seedParticipantsFromLegacy(project);

  async function doRemove(contactId: string, role: string) {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await removeParticipant(project.pid, contactId, role);
      onMsg('Participant removed.');
    } catch (e) {
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function doAdd() {
    if (!pickContactId || !isParticipantRole(pickRole)) return;
    setBusy(true);
    try {
      await addParticipant(project.pid, {
        contactId: pickContactId,
        role: pickRole,
        relationship: pickRelationship.trim() || undefined,
        addedAt: new Date().toISOString(),
        addedBy: actorUid ?? undefined,
      });
      setPickContactId('');
      setPickRelationship('');
      setAddOpen(false);
      onMsg('Participant added.');
    } catch (e) {
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const clientById = new Map(clients.map((c) => [c.cid, c]));

  return (
    <Box title="Participants" note="People on this project (AD-009)">
      <p style={{ fontSize: 11.5, color: 'var(--ink-mute)', margin: '0 0 10px', lineHeight: 1.5 }}>
        This panel links contacts (existing clients/partners) to this project for
        collaboration and visibility. It is <b>not</b> where you add family members
        for the L1A report — those come from the <em>client's intake form</em>{' '}
        (Sections E &amp; F draw from the intake). To add a family member as a
        participant here, they must already exist as a contact.
      </p>
      {participants.length === 0 && (
        <p style={{ fontSize: 13, color: 'var(--ink-mute)' }}>
          No participants yet — seeding from client/partner links.
        </p>
      )}
      {participants.map((entry) => {
        const contact = clientById.get(entry.contactId);
        const isPrimary = entry.role === 'primary_client';
        return (
          <div
            key={`${entry.contactId}::${entry.role}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              borderBottom: '1px solid var(--rule)',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ flex: '1 1 160px', fontSize: 13.5 }}>
              {contact?.name || entry.contactId}
              <span style={{ fontSize: 12, color: 'var(--ink-mute)', marginLeft: 6 }}>
                {contact?.clientNo}
              </span>
            </span>
            <Tag kind="val">{participantLabel(entry.role)}</Tag>
            {entry.relationship && (
              <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                {entry.relationship}
              </span>
            )}
            {isAdmin && !isPrimary && (
              <Btn3
                intent="ghost"
                size="sm"
                onClick={() => doRemove(entry.contactId, entry.role)}
                disabled={busy}
              >
                Remove
              </Btn3>
            )}
            {isPrimary && (
              <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                primary
              </span>
            )}
          </div>
        );
      })}

      {isAdmin && (
        <div style={{ marginTop: 12 }}>
          {!addOpen ? (
            <Btn3 intent="ink" size="sm" onClick={() => setAddOpen(true)} disabled={busy}>
              + Add participant
            </Btn3>
          ) : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <select
                value={pickContactId}
                onChange={(e) => setPickContactId(e.target.value)}
                style={{ fontSize: 13, padding: '6px 8px', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--ink)', minWidth: 200 }}
              >
                <option value="">Choose contact…</option>
                {clients.map((c) => (
                  <option key={c.cid} value={c.cid}>
                    {c.name} ({c.clientNo})
                  </option>
                ))}
              </select>
              <select
                value={pickRole}
                onChange={(e) => setPickRole(e.target.value as ParticipantRole)}
                style={{ fontSize: 13, padding: '6px 8px', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--ink)' }}
              >
                {(['co_client','family','consultant','partner','staff'] as ParticipantRole[]).map((r) => (
                  <option key={r} value={r}>{participantLabel(r)}</option>
                ))}
              </select>
              <input
                value={pickRelationship}
                onChange={(e) => setPickRelationship(e.target.value)}
                placeholder="Relationship (optional)"
                style={{ fontSize: 13, padding: '6px 8px', border: '1px solid var(--rule)', borderRadius: 'var(--r-sm)', background: 'var(--surface)', color: 'var(--ink)', minWidth: 160 }}
              />
              <Btn3 intent="grn" size="sm" onClick={doAdd} disabled={busy || !pickContactId}>
                {busy ? 'Adding…' : 'Add'}
              </Btn3>
              <Btn3 intent="ghost" size="sm" onClick={() => setAddOpen(false)} disabled={busy}>
                Cancel
              </Btn3>
            </div>
          )}
        </div>
      )}
    </Box>
  );
}

/* ════════════════════ Phase 4 — Per-Project Access Tab (AD-013) ════════════════════
 *
 * Mini 6-role × 8-section matrix with per-project override controls.
 * Admin-only: toggle overrides. Staff: view-only.
 *
 * Default cells = global matrix value. Custom cells = project override value.
 * "Clear all custom overrides" button resets to global defaults.
 */

const ROLE_LABELS_SHORT: Record<PortalRole, string> = {
  primary_client: 'Primary',
  co_client: 'Co-client',
  family: 'Family',
  consultant: 'Consultant',
  partner: 'Partner',
  staff: 'Staff',
  admin: 'Admin',
};

const SECTION_LABELS_SHORT: Record<PortalSection, string> = {
  project_status: 'Status',
  floor_plan_grid: 'Floor/Grid',
  concerns_goals: 'Concerns',
  auo_observations: 'AUO',
  delivered_report: 'Report',
  payment_details: 'Payment',
  internal_chat: 'Int.Chat',
  project_files: 'Files',
};

function AccessPanel({
  project,
  isAdmin,
  actorUid,
  onMsg,
}: {
  project: LiveProject;
  isAdmin: boolean;
  actorUid: string | null;
  onMsg: (msg: string) => void;
}) {
  const [globalMatrix, setGlobalMatrix] = useState<VisibilityMatrix | null>(null);
  const [busy, setBusy] = useState(false);

  // Load the global matrix once.
  useEffect(() => {
    getVisibilityMatrix().then(setGlobalMatrix).catch(() => setGlobalMatrix(APPROVED_DEFAULTS));
  }, []);

  const overrides: ProjectVisibilityOverrides =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((project as unknown as Record<string, unknown>).visibilityOverrides as ProjectVisibilityOverrides) ?? {};
  const matrix = globalMatrix ?? APPROVED_DEFAULTS;

  async function handleToggle(role: PortalRole, section: PortalSection, value: boolean) {
    if (!isAdmin || !actorUid) return;
    if (role === 'admin') return;
    setBusy(true);
    try {
      await setProjectVisibilityOverride(project.pid, role, section, value, actorUid);
      onMsg(`${ROLE_LABELS_SHORT[role]} → ${SECTION_LABELS_SHORT[section]}: ${value ? 'visible' : 'hidden'} (project override).`);
    } catch (e) {
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleClear(role: PortalRole, section: PortalSection) {
    if (!isAdmin || !actorUid) return;
    setBusy(true);
    try {
      await clearProjectVisibilityOverride(project.pid, role, section, actorUid);
      onMsg(`${ROLE_LABELS_SHORT[role]} → ${SECTION_LABELS_SHORT[section]}: reset to global default.`);
    } catch (e) {
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleClearAll() {
    if (!isAdmin || !actorUid) return;
    const ok = window.confirm('Clear all project-level visibility overrides? All roles revert to the global defaults.');
    if (!ok) return;
    setBusy(true);
    try {
      await clearAllProjectOverrides(project.pid, actorUid);
      onMsg('All project-level overrides cleared.');
    } catch (e) {
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const hasAnyOverride = PORTAL_ROLES.some(
    (r) => PORTAL_SECTIONS.some((s) => {
      const ov = (overrides as Record<string, Record<string, boolean | undefined> | undefined>)[r];
      return ov != null && s in ov && typeof ov[s] === 'boolean';
    }),
  );

  // Show only non-admin roles in this mini matrix (admin is always all-true).
  const displayRoles = PORTAL_ROLES.filter((r) => r !== 'admin');

  return (
    <Box
      title="Access"
      note="Per-project visibility overrides — defaults come from Settings → Portal Access"
    >
      {isAdmin && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <Btn3
            intent="ghost"
            size="sm"
            onClick={handleClearAll}
            disabled={busy || !hasAnyOverride}
          >
            Clear all custom overrides
          </Btn3>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
          <thead>
            <tr>
              <th
                style={{
                  textAlign: 'left',
                  fontWeight: 600,
                  fontSize: 11,
                  color: 'var(--ink-mute)',
                  padding: '4px 10px 4px 0',
                  whiteSpace: 'nowrap',
                }}
              >
                Role
              </th>
              {PORTAL_SECTIONS.map((s) => (
                <th
                  key={s}
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--ink-mute)',
                    textAlign: 'center',
                    padding: '4px 4px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {SECTION_LABELS_SHORT[s]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRoles.map((r) => (
              <tr key={r} style={{ borderBottom: '1px solid var(--rule)' }}>
                <td
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '5px 10px 5px 0',
                    whiteSpace: 'nowrap',
                    color: 'var(--ink)',
                  }}
                >
                  {ROLE_LABELS_SHORT[r]}
                </td>
                {PORTAL_SECTIONS.map((s) => {
                  const projRoleOv = (overrides as Record<string, Record<string, boolean | undefined> | undefined>)[r];
                  const hasOverride =
                    projRoleOv != null && s in projRoleOv && typeof projRoleOv[s] === 'boolean';
                  const effectiveValue = canSee(r, s, matrix, overrides);
                  // r is never 'admin' here (admin excluded from displayRoles above)
                  const isHardLocked = s === 'internal_chat';

                  return (
                    <td
                      key={s}
                      style={{
                        textAlign: 'center',
                        padding: '5px 4px',
                        verticalAlign: 'middle',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 2,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={effectiveValue}
                          disabled={!isAdmin || isHardLocked || busy}
                          onChange={(e) => handleToggle(r, s, e.target.checked)}
                          style={{
                            width: 14,
                            height: 14,
                            cursor: isAdmin && !isHardLocked ? 'pointer' : 'not-allowed',
                            opacity: isHardLocked ? 0.4 : 1,
                          }}
                        />
                        {hasOverride && (
                          <span
                            title="Project override — click to clear"
                            onClick={() => isAdmin && handleClear(r, s)}
                            style={{
                              fontSize: 9,
                              color: '#b45309',
                              cursor: isAdmin ? 'pointer' : 'default',
                              background: '#fffbeb',
                              border: '1px solid #fde68a',
                              borderRadius: 3,
                              padding: '0 3px',
                              lineHeight: 1.5,
                            }}
                          >
                            custom
                          </span>
                        )}
                        {!hasOverride && (
                          <span
                            style={{
                              fontSize: 9,
                              color: 'var(--ink-mute)',
                              lineHeight: 1.5,
                            }}
                          >
                            default
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 11, color: 'var(--ink-mute)', marginTop: 10 }}>
        "Custom" cells override the global default for this project only.
        Click a "custom" badge to revert that cell to the global setting.
      </p>
    </Box>
  );
}
